import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../Modal';
import { useCart } from '../../contexts/CartContext';
import { useStore } from '../../contexts/StoreContext';
import { useToast } from '../../contexts/ToastContext';
import { useComandaSession } from '../../contexts/ComandaSessionContext';
import { useStatus } from '../../contexts/StatusContext';
import { CartItemImage } from '../CartItemImage';
import { OrderService, OrderItem } from '../../services/OrderService';
import { TrackingService } from '../../services/TrackingService';
import { PaymentService } from '../../services/PaymentService';
import { Environment } from '../../environment';
import { PaymentBrick } from './PaymentBrick';
import {
  CartEmpty, EmptyIcon, EmptyText, CartItems, CartItem, ItemHeader, ItemInfo, ItemName, ItemPrice, QuantityControl, QuantityButton, Quantity, RemoveButton, DuplicateButton, KeepShoppingButton, ItemOptions, OptionLabel, OptionsGroup, OptionChip, OptionTable, OptionRow, OptionCheckbox, OptionRowInfo, OptionRowName, OptionRowDesc, OptionRowPrice, DeliverySection, SectionTitle, DeliveryOptions, DeliveryOption, DeliveryInfo, DeliveryLabel, DeliveryFee, PickupAddressSection, PickupAddressTitle, PickupAddressText, TotalSection, TotalRow, TotalLabel, TotalValue, PaymentSection, PaymentOptions, PaymentOption, FormSection, Input, Textarea, CheckoutButton, WhatsAppIcon, PixContainer, PixTitle, PixKeyWrapper, PixKey, CopyButton
} from './Styled';

interface CartModalProps { isOpen: boolean; onClose: () => void; }
type DeliveryType = 'delivery' | 'pickup' | 'table' | 'comanda-simples' | 'comanda-cartao';
interface FormData { name: string; phone: string; address: string; numero: string; complemento: string; bairro: string; reference: string; paymentMethod: 'pix' | 'card' | 'cash'; changeFor?: string; mesa?: string; password?: string; isInterior?: boolean; kmInterior?: string; }

function CollapsibleItemOptions({ label, children, closeWhen }: { label: React.ReactNode; children: React.ReactNode; closeWhen?: boolean }) {
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!closeWhen) return;
    const t = setTimeout(() => setOpen(false), 280);
    return () => clearTimeout(t);
  }, [closeWhen]);

  return (
    <ItemOptions>
      <div
        role="button"
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          cursor: 'pointer', userSelect: 'none',
          padding: '8px 12px',
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
        }}
      >
        <OptionLabel style={{ margin: 0, flex: 1 }}>{label}</OptionLabel>
        <div style={{
          width: '24px', height: '24px', borderRadius: '50%',
          background: open ? 'var(--primary, #f58220)' : '#e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, transition: 'background 0.25s ease',
        }}>
          <span style={{
            display: 'inline-block',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.25s ease',
            color: open ? 'white' : '#94a3b8',
            fontSize: '12px', lineHeight: 1,
          }}>▾</span>
        </div>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateRows: open ? '1fr' : '0fr',
        transition: 'grid-template-rows 0.3s ease',
      }}>
        <div style={{ overflow: 'hidden', minHeight: 0, paddingTop: '6px' }}>
          {children}
        </div>
      </div>
    </ItemOptions>
  );
}

export const CartModal: React.FC<CartModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem, duplicateItem, clearCart, subtotal, total, deliveryFee: cartDeliveryFee, setDeliveryFee, applyCupom, removeCupom, appliedCupom, discountAmount, promotionDiscount } = useCart();

  const { store, sauces, sideDishes, flavors, flavorLists, flavorClassifications, additionals, additionalLists, ingredients: allIngredients, ingredientLists, sauceLists, sideDishLists, salads, saladLists, desserts, dessertLists, products, allProducts } = useStore();
  const { showToast } = useToast();
  const { comandaMode, mesaNumber: comandaMesaNumber, cartaoCode, setComandaId } = useComandaSession();
  const { isGestorMode } = useStatus();

  const finalizeGestor = () => {
    showToast('Pedido registrado!', 'success');
    clearCart();
    window.location.reload();
  };

  const [couponCode, setCouponCode] = useState('');
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [deliveryType, setDeliveryType] = useState<DeliveryType>('delivery');
  const [hasActiveComandaSession, setHasActiveComandaSession] = useState(false);
  const [formData, setFormData] = useState<FormData>({ name: '', phone: '', address: '', numero: '', complemento: '', bairro: '', reference: '', paymentMethod: 'pix', mesa: '', password: '', isInterior: false, kmInterior: '' });

  // Estados para o fluxo de pagamento automatizado
  const [paymentStep, setPaymentStep] = useState<'info' | 'payment' | 'pix' | 'classic-pix' | 'payment_confirmed' | 'success'>('info');
  const [preferenceId, setPreferenceId] = useState<string | null>(null);
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [currentAbandonedCartId, setCurrentAbandonedCartId] = useState<string | null>(null);
  const [pixData, setPixData] = useState<{ paymentId: string; qr_code: string; qr_code_base64: string } | null>(null);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const [showBandeirasModal, setShowBandeirasModal] = useState(false);
  const [ingredientConfirm, setIngredientConfirm] = useState<{ itemId: string; ingredient: string } | null>(null);
  const [selectedFiscalPM, setSelectedFiscalPM] = useState<any>(null);
  const [selectedVariacaoIdx, setSelectedVariacaoIdx] = useState<number>(-1);
  const pendingWaMessage = useRef('');
  const pendingOrderNumber = useRef<string | null>(null);

  useEffect(() => {
    setSelectedFiscalPM(null);
    setSelectedVariacaoIdx(-1);
  }, [deliveryType]);

  const variacaoNome = (v: any) => (typeof v === 'string' ? v : v?.nome || '');

  const isAutomated = store?.paymentMethod === 'automated' && !!store?.mercadoPagoConfig?.publicKey;
  const isClassicPix = !isAutomated && !(store as any)?.paymentMethodsReady && formData.paymentMethod === 'pix' && !!(store as any)?.pixKey && deliveryType !== 'table' && deliveryType !== 'comanda-simples' && deliveryType !== 'comanda-cartao';
  const requirePaymentBeforeOrder = isAutomated && !!store?.requirePaymentBeforeOrder;
  const maxInstallments: number = (store as any)?.maxInstallments || 1;
  const allowCashPayment: boolean = !!(store as any)?.allowCashPayment;
  const isCashSelected = formData.paymentMethod === 'cash';
  const isClassicCard = (store as any)?.cardMode === 'classic';
  const isCardSelected = formData.paymentMethod === 'card';
  const cardBrands = useMemo(() => {
    const allBrands: string[] = [];
    (store?.paymentMethods || []).forEach(pm => {
      (pm.variacoes || []).forEach((v: any) => {
        const n = variacaoNome(v);
        if (n && !allBrands.includes(n)) allBrands.push(n);
      });
    });
    return allBrands;
  }, [store?.paymentMethods]);

  const fiscalPaymentMethods = useMemo(() => {
    if (!(store as any)?.paymentMethodsReady) return [];
    const methods: any[] = (store as any)?.paymentMethods || [];
    return methods;
  }, [store]);

  const visibleFiscalMethods = useMemo(() => {
    if (!fiscalPaymentMethods.length) return [];
    if (deliveryType === 'delivery' || deliveryType === 'pickup') {
      return fiscalPaymentMethods.filter((pm: any) => pm.disponivelDelivery);
    }
    return fiscalPaymentMethods;
  }, [fiscalPaymentMethods, deliveryType]);

  // Cartão no modo clássico sai pelo WhatsApp mesmo em modo automatizado. Modos QR comanda sempre pelo fluxo próprio.
  const shouldUseAutomatedFlow = isAutomated && deliveryType !== 'table' && deliveryType !== 'comanda-simples' && deliveryType !== 'comanda-cartao' && !isCashSelected && !(isCardSelected && isClassicCard);

  const handleCopyPix = (text: string) => {
    if (!text) return;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => showToast('Chave PIX copiada!', 'success')).catch(() => fallbackCopy(text));
    } else fallbackCopy(text);
  };

  const fallbackCopy = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed"; textArea.style.left = "-999999px"; textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus(); textArea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) showToast('Chave PIX copiada!', 'success');
      else showToast('Não foi possível copiar. Selecione e copie manualmente.', 'error');
    } catch (err) { showToast('Erro ao copiar a chave.', 'error'); }
    document.body.removeChild(textArea);
  };

  const getProductFlavors = useCallback((product: any) => {
    if (!product.hasFlavor) return [];
    const listId = (product as any).flavorListId?._id || product.flavorListId;
    if (listId) {
      const list = (flavorLists || []).find((l: any) => String(l.id || l._id) === String(listId));
      if (list && list.flavors && Array.isArray(list.flavors)) {
        return list.flavors.map((f: any) => (typeof f === 'object' ? { ...f, id: f.id || f._id } : { id: f, name: f, price: 0 }));
      }
      return [];
    }
    return (flavors || []).map((f: any) => ({ ...f, id: f.id || f._id }));
  }, [flavors, flavorLists]);

  const getProductAdditionals = useCallback((product: any) => {
    if (!product.hasAdditional) return [];
    const listId = (product as any).additionalListId?._id || product.additionalListId;
    if (listId) {
        const list = (additionalLists || []).find((l: any) => String(l.id || l._id) === String(listId));
        if (list && list.additionals && Array.isArray(list.additionals)) {
            return list.additionals.map((a: any) => (typeof a === 'object' ? { ...a, id: a.id || a._id } : { id: a, name: a, price: 0 }));
        }
        return [];
    }
    return (additionals || []).map((a: any) => ({ ...a, id: a.id || a._id }));
  }, [additionals, additionalLists]);

  const getProductIncludedAdditionals = useCallback((product: any) => {
    if (!(product as any).hasIncludedAdditionals) return [];
    const listId = (product as any).includedAdditionalListId?._id || (product as any).includedAdditionalListId;
    if (listId) {
      const list = (additionalLists || []).find((l: any) => String(l.id || l._id) === String(listId));
      if (list && list.additionals && Array.isArray(list.additionals)) {
        return list.additionals.map((a: any) => (typeof a === 'object' ? { ...a, id: a.id || a._id } : { id: a, name: a, price: 0 }));
      }
      return [];
    }
    return (additionals || []).map((a: any) => ({ ...a, id: a.id || a._id }));
  }, [additionals, additionalLists]);

  const getExtrasChargeAdditionals = useCallback((product: any) => {
    if (!product.hasAdditional) return true;
    const listId = (product as any).additionalListId?._id || product.additionalListId;
    if (listId) {
      const list = (additionalLists || []).find((l: any) => String(l.id || l._id) === String(listId));
      return (list as any)?.chargeAdditionals !== false;
    }
    return true;
  }, [additionalLists]);

  const getProductSauces = useCallback((product: any) => {
    if (!product.hasSauce) return [];
    const listId = (product as any).sauceListId?._id || product.sauceListId;
    if (listId) {
      const list = (sauceLists || []).find((l: any) => String(l.id || l._id) === String(listId));
      if (list && list.sauces && Array.isArray(list.sauces)) {
        return list.sauces.map((s: any) => (typeof s === 'object' ? { ...s, id: s.id || s._id } : { id: s, name: s, price: 0 }));
      }
      return [];
    }
    return (sauces || []).map((s: any) => ({ ...s, id: s.id || s._id }));
  }, [sauces, sauceLists]);

  const getProductSideDishes = useCallback((product: any) => {
    if (!product.hasSideDish) return [];
    const listId = (product as any).sideDishListId?._id || product.sideDishListId;
    if (listId) {
      const list = (sideDishLists || []).find((l: any) => String(l.id || l._id) === String(listId));
      if (list && list.sideDishes && Array.isArray(list.sideDishes)) {
        return list.sideDishes.map((s: any) => (typeof s === 'object' ? { ...s, id: s.id || s._id } : { id: s, name: s, price: 0 }));
      }
      return [];
    }
    return (sideDishes || []).map((s: any) => ({ ...s, id: s.id || s._id }));
  }, [sideDishes, sideDishLists]);

  const getProductSalads = useCallback((product: any) => {
    if (!product.hasSalad) return [];
    const listId = (product as any).saladListId?._id || product.saladListId;
    if (listId) {
      const list = (saladLists || []).find((l: any) => String(l.id || l._id) === String(listId));
      if (list && list.salads && Array.isArray(list.salads)) {
        return list.salads.map((s: any) => (typeof s === 'object' ? { ...s, id: s.id || s._id } : { id: s, name: s, price: 0 }));
      }
      return [];
    }
    return (salads || []).map((s: any) => ({ ...s, id: s.id || s._id }));
  }, [salads, saladLists]);

  const getProductDesserts = useCallback((product: any) => {
    if (!product.hasDessert) return [];
    const listId = (product as any).dessertListId?._id || product.dessertListId;
    if (listId) {
      const list = (dessertLists || []).find((l: any) => String(l.id || l._id) === String(listId));
      if (list && list.desserts && Array.isArray(list.desserts)) {
        return list.desserts.map((d: any) => (typeof d === 'object' ? { ...d, id: d.id || d._id } : { id: d, name: d, price: 0 }));
      }
      return [];
    }
    return (desserts || []).map((d: any) => ({ ...d, id: d.id || d._id }));
  }, [desserts, dessertLists]);

  const toggleSauce = useCallback((itemId: string, sauceName: string, product: any) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const current = (item.options?.sauces || []) as string[];
    const isSelected = current.includes(sauceName);
    const limit = product.sauceLimit || 1;
    if (!isSelected && current.length >= limit) {
      if (limit === 1) updateQuantity(itemId, item.quantity, { ...item.options, sauces: [sauceName] });
      else showToast(`Você só pode escolher até ${limit} molho(s)`, 'warning');
      return;
    }
    const newList = isSelected ? current.filter(s => s !== sauceName) : [...current, sauceName];
    updateQuantity(itemId, item.quantity, { ...item.options, sauces: newList });
  }, [items, updateQuantity, showToast]);

  const updateSauceQty = useCallback((itemId: string, sauceName: string, delta: number, product: any) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const qtys = { ...(item.options?.sauceQtys || {}) } as Record<string, number>;
    const newQty = Math.max(0, (qtys[sauceName] || 0) + delta);
    const limit = product.sauceLimit || 0;
    if (delta > 0 && limit > 0) {
      const total = Object.entries(qtys).filter(([n]) => n !== sauceName).reduce((s, [, q]) => s + (q as number), 0) + newQty;
      if (total > limit) { showToast(`Limite de ${limit} molho(s) atingido`, 'warning'); return; }
    }
    qtys[sauceName] = newQty;
    const newSauces = Object.entries(qtys).filter(([, q]) => (q as number) > 0).map(([n]) => n);
    updateQuantity(itemId, item.quantity, { ...item.options, sauces: newSauces, sauceQtys: qtys });
  }, [items, updateQuantity, showToast]);

  const toggleFlavor = useCallback((itemId: string, flavorName: string, product: any) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const current = (item.options?.flavors || []) as string[];
    const isSelected = current.includes(flavorName);
    const limit = product.flavorLimit || 1;
    if (!isSelected && current.length >= limit) {
      if (limit === 1) updateQuantity(itemId, item.quantity, { ...item.options, flavors: [flavorName] });
      else showToast(`Você só pode escolher até ${limit} sabor(es)`, 'warning');
      return;
    }
    const newList = isSelected ? current.filter(f => f !== flavorName) : [...current, flavorName];
    updateQuantity(itemId, item.quantity, { ...item.options, flavors: newList });
  }, [items, updateQuantity, showToast]);

  const toggleSideDish = useCallback((itemId: string, sideDishName: string, product: any) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const current = (item.options?.sideDishes || []) as string[];
    const isSelected = current.includes(sideDishName);
    const limit = product.sideDishLimit || 1;
    if (!isSelected && current.length >= limit) {
      if (limit === 1) updateQuantity(itemId, item.quantity, { ...item.options, sideDishes: [sideDishName] });
      else {
        const noun = product.sideDishAsPrepMode ? 'modo(s) de preparo' : 'acompanhamento(s)';
        showToast(`Você só pode escolher até ${limit} ${noun}`, 'warning');
      }
      return;
    }
    const newList = isSelected ? current.filter(s => s !== sideDishName) : [...current, sideDishName];
    updateQuantity(itemId, item.quantity, { ...item.options, sideDishes: newList });
  }, [items, updateQuantity, showToast]);

  const changeAdditionalQty = useCallback((itemId: string, additionalName: string, delta: number, product: any) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const qtys = { ...(item.options?.additionalQtys || {}) } as Record<string, number>;
    const currentQty = qtys[additionalName] || 0;
    const newQty = Math.max(0, currentQty + delta);
    const limit = product.additionalLimit || 0;

    if (delta > 0 && limit > 0) {
      const totalQty = Object.values(qtys).reduce((s: number, q: number) => s + q, 0);
      if (totalQty >= limit) { showToast(`Você só pode escolher até ${limit} adicional(ais)`, 'warning'); return; }
    }

    if (newQty === 0) delete qtys[additionalName];
    else qtys[additionalName] = newQty;

    const expanded: string[] = [];
    Object.entries(qtys).forEach(([name, qty]) => { for (let i = 0; i < qty; i++) expanded.push(name); });

    updateQuantity(itemId, item.quantity, { ...item.options, additionals: expanded, additionalQtys: qtys });
  }, [items, updateQuantity, showToast]);

  const changeIncludedAdditionalQty = useCallback((itemId: string, additionalName: string, delta: number, product: any) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const qtys = { ...(item.options?.includedAdditionalQtys || {}) } as Record<string, number>;
    const currentQty = qtys[additionalName] || 0;
    const newQty = Math.max(0, currentQty + delta);
    const limit = (product as any).includedAdditionalLimit || 1;

    if (delta > 0) {
      const totalQty = Object.values(qtys).reduce((s: number, q: number) => s + q, 0);
      if (totalQty >= limit) { showToast(`Você só pode escolher até ${limit} adicional(ais) incluso(s)`, 'warning'); return; }
    }

    if (newQty === 0) delete qtys[additionalName];
    else qtys[additionalName] = newQty;

    const expanded: string[] = [];
    Object.entries(qtys).forEach(([name, qty]) => { for (let i = 0; i < qty; i++) expanded.push(name); });

    updateQuantity(itemId, item.quantity, { ...item.options, includedAdditionals: expanded, includedAdditionalQtys: qtys });
  }, [items, updateQuantity, showToast]);

  const toggleSalad = useCallback((itemId: string, saladName: string, product: any) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const current = (item.options?.salads || []) as string[];
    const isSelected = current.includes(saladName);
    const limit = product.saladLimit || 1;
    if (!isSelected && current.length >= limit) {
      if (limit === 1) updateQuantity(itemId, item.quantity, { ...item.options, salads: [saladName] });
      else showToast(`Você só pode escolher até ${limit} salada(s)`, 'warning');
      return;
    }
    const newList = isSelected ? current.filter(s => s !== saladName) : [...current, saladName];
    updateQuantity(itemId, item.quantity, { ...item.options, salads: newList });
  }, [items, updateQuantity, showToast]);

  const toggleDessert = useCallback((itemId: string, dessertName: string, product: any) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const current = (item.options?.desserts || []) as string[];
    const isSelected = current.includes(dessertName);
    const limit = product.dessertLimit || 1;
    if (!isSelected && current.length >= limit) {
      if (limit === 1) updateQuantity(itemId, item.quantity, { ...item.options, desserts: [dessertName] });
      else showToast(`Você só pode escolher até ${limit} sobremesa(s)`, 'warning');
      return;
    }
    const newList = isSelected ? current.filter(d => d !== dessertName) : [...current, dessertName];
    updateQuantity(itemId, item.quantity, { ...item.options, desserts: newList });
  }, [items, updateQuantity, showToast]);

  const toggleIngredient = useCallback((itemId: string, ingredient: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const current = (item.options?.removedIngredients || []) as string[];
    const newList = current.includes(ingredient) ? current.filter(i => i !== ingredient) : [...current, ingredient];
    updateQuantity(itemId, item.quantity, { ...item.options, removedIngredients: newList });
  }, [items, updateQuantity]);

  const toggleSimpleAdditional = useCallback((itemId: string, additionalName: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const current = (item.options?.additionals || []) as string[];
    const isSelected = current.includes(additionalName);
    const newList = isSelected ? current.filter(a => a !== additionalName) : [...current, additionalName];
    const newQtys = Object.fromEntries(newList.map(n => [n, 1]));
    updateQuantity(itemId, item.quantity, { ...item.options, additionals: newList, additionalQtys: newQtys });
  }, [items, updateQuantity]);

  // --- COMBO SLOT HANDLERS ---
  const getComboSlot = useCallback((item: any, slotKey: string) =>
    (item.options?.comboSelections || {})[slotKey] || {}, []);

  const setComboSlotField = useCallback((itemId: string, slotKey: string, field: string, value: any) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const current = item.options?.comboSelections || {};
    const slot = current[slotKey] || {};
    updateQuantity(itemId, item.quantity, {
      ...item.options,
      comboSelections: { ...current, [slotKey]: { ...slot, [field]: value } }
    });
  }, [items, updateQuantity]);

  const toggleComboFlavor = useCallback((itemId: string, slotKey: string, name: string, limit: number) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const current: string[] = (item.options?.comboSelections || {})[slotKey]?.flavors || [];
    if (current.includes(name)) { setComboSlotField(itemId, slotKey, 'flavors', current.filter(n => n !== name)); return; }
    if (limit === 1) { setComboSlotField(itemId, slotKey, 'flavors', [name]); return; }
    if (current.length < limit) { setComboSlotField(itemId, slotKey, 'flavors', [...current, name]); return; }
    showToast(`Limite de ${limit} sabor(es) atingido`, 'warning');
  }, [items, setComboSlotField, showToast]);

  const toggleComboSauce = useCallback((itemId: string, slotKey: string, name: string, limit: number) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const current: string[] = (item.options?.comboSelections || {})[slotKey]?.sauces || [];
    if (current.includes(name)) { setComboSlotField(itemId, slotKey, 'sauces', current.filter(n => n !== name)); return; }
    if (limit === 1) { setComboSlotField(itemId, slotKey, 'sauces', [name]); return; }
    if (current.length < limit) { setComboSlotField(itemId, slotKey, 'sauces', [...current, name]); return; }
    showToast(`Limite de ${limit} molho(s) atingido`, 'warning');
  }, [items, setComboSlotField, showToast]);

  const toggleComboSideDish = useCallback((itemId: string, slotKey: string, name: string, limit: number) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const current: string[] = (item.options?.comboSelections || {})[slotKey]?.sideDishes || [];
    if (current.includes(name)) { setComboSlotField(itemId, slotKey, 'sideDishes', current.filter(n => n !== name)); return; }
    if (limit === 1) { setComboSlotField(itemId, slotKey, 'sideDishes', [name]); return; }
    if (current.length < limit) { setComboSlotField(itemId, slotKey, 'sideDishes', [...current, name]); return; }
    showToast(`Limite de ${limit} acompanhamento(s) atingido`, 'warning');
  }, [items, setComboSlotField, showToast]);

  const changeComboAdditionalQty = useCallback((itemId: string, slotKey: string, name: string, delta: number, limit: number) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const slot = (item.options?.comboSelections || {})[slotKey] || {};
    const qtys = { ...(slot.additionalQtys || {}) } as Record<string, number>;
    const current = qtys[name] || 0;
    if (delta > 0 && limit > 0) {
      const total = Object.values(qtys).reduce((s, q) => s + q, 0);
      if (total >= limit) { showToast(`Limite de ${limit} adicional(ais) atingido`, 'warning'); return; }
    }
    const next = current + delta;
    if (next <= 0) delete qtys[name]; else qtys[name] = next;
    setComboSlotField(itemId, slotKey, 'additionalQtys', qtys);
  }, [items, setComboSlotField, showToast]);

  useEffect(() => {
    if (isOpen) { setSelectedFiscalPM(null); setSelectedVariacaoIdx(-1); }
  }, [isOpen]);

  useEffect(() => {
    // Re-lê sessionStorage ao montar e quando o modal abre — garante pré-preenchimento
    // mesmo quando CartRecovery seta os dados após a montagem do componente
    const savedName = sessionStorage.getItem('comanda_name');
    const savedPhone = sessionStorage.getItem('comanda_phone');
    const savedMesa = sessionStorage.getItem('comanda_mesa');
    const savedPassword = sessionStorage.getItem('comanda_password');
    const aiAddress = sessionStorage.getItem('ai_customer_address');
    const aiReference = sessionStorage.getItem('ai_customer_reference');
    const aiDeliveryMode = sessionStorage.getItem('ai_customer_delivery_mode');

    // Modos de comanda via QR Code têm prioridade
    if (comandaMode === 'simples') {
      setDeliveryType('comanda-simples');
      const simplesId = sessionStorage.getItem('comanda_simples_id');
      setHasActiveComandaSession(!!simplesId);
      setFormData(prev => ({ ...prev, name: savedName || prev.name }));
    } else if (comandaMode === 'cartao') {
      setDeliveryType('comanda-cartao');
      const cartaoSession = cartaoCode ? sessionStorage.getItem(`comanda_cartao_${cartaoCode}`) : null;
      setHasActiveComandaSession(!!cartaoSession);
      setFormData(prev => ({ ...prev, name: savedName || prev.name }));
    } else if (savedMesa && !store?.clienteNaoFazPedidos) {
      // Fluxo comanda/mesa (garçom pelo Gestor)
      setFormData(prev => ({ ...prev, name: savedName || prev.name, phone: savedPhone || prev.phone, mesa: savedMesa, password: savedPassword || prev.password }));
      setDeliveryType('table');
    } else {
      // Pré-preenchimento normal (comanda anterior ou recuperação AI agent)
      // Só aplica campos que ainda estão vazios para não sobrescrever dados já digitados pelo usuário
      setFormData(prev => {
        const updates: Partial<FormData> = {};
        if (savedName && !prev.name) updates.name = savedName;
        if (savedPhone && !prev.phone) updates.phone = savedPhone;
        if (aiAddress && !prev.address) updates.address = aiAddress;
        if (aiReference && !prev.reference) updates.reference = aiReference;
        return Object.keys(updates).length ? { ...prev, ...updates } : prev;
      });
      if (aiDeliveryMode === 'retirada') setDeliveryType('pickup');
      else if (aiDeliveryMode === 'entrega') setDeliveryType('delivery');
    }
  }, [isOpen, comandaMode]);

  const deliveryFee = (() => {
    if (deliveryType !== 'delivery') return 0;
    const storeAny = store as any;
    if (storeAny?.deliveryFeeType === 'variable' && storeAny?.deliveryNeighborhoods?.length) {
      if (formData.bairro === '__interior__' && storeAny?.aceitaInterior) {
        const km = parseFloat(formData.kmInterior || '0') || 0;
        return parseFloat((km * (storeAny.interiorValorPorKm || 0)).toFixed(2));
      }
      if (formData.bairro) {
        const nb = storeAny.deliveryNeighborhoods.find((n: any) => n.nome === formData.bairro);
        return nb ? nb.valor : (store?.deliveryFee || 0);
      }
    }
    return store?.deliveryFee || 0;
  })();
  const deliveryAddress = (() => {
    if (deliveryType !== 'delivery') return formData.address;
    const bairroDisplay = formData.bairro === '__interior__' ? 'Interior' : formData.bairro;
    const parts = [formData.address, formData.numero, formData.complemento, bairroDisplay].filter(Boolean);
    const base = parts.join(', ');
    if (formData.bairro === '__interior__' && formData.kmInterior) return `${base} (${formData.kmInterior} km)`;
    return base;
  })();
  const baseTotal = Math.max(0, subtotal - discountAmount - promotionDiscount) + deliveryFee;
  const paymentFee = useMemo(() => {
    if (!selectedFiscalPM || !(store as any)?.paymentMethodsReady) return 0;
    const variacoes: any[] = selectedFiscalPM.variacoes || [];
    const config = variacoes.length > 0 && selectedVariacaoIdx >= 0
      ? variacoes[selectedVariacaoIdx]
      : selectedFiscalPM;
    if (!config?.taxaAtiva) return 0;
    const taxa = parseFloat(config?.taxaValor) || 0;
    if (!taxa || (config?.taxaPagador || 'logista') !== 'cliente') return 0;
    return (config?.taxaTipo || 'percentual') === 'percentual'
      ? parseFloat((baseTotal * taxa / 100).toFixed(2))
      : taxa;
  }, [selectedFiscalPM, selectedVariacaoIdx, baseTotal, store]);
  const localTotal = baseTotal + paymentFee;
  useEffect(() => { setDeliveryFee(deliveryFee); }, [isOpen, deliveryFee, setDeliveryFee]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setIsApplyingCoupon(true);
    try {
      await applyCupom(couponCode, formData.phone.replace(/\D/g, ''), deliveryType === 'table');
      setCouponCode('');
    } catch (err) {} finally { setIsApplyingCoupon(false); }
  };

  const handlePhoneChange = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    let formatted = '';
    if (numbers.length > 0) formatted = '(' + numbers.substring(0, 2);
    if (numbers.length >= 3) formatted += ') ' + numbers.substring(2, 7);
    if (numbers.length >= 8) formatted += '-' + numbers.substring(7, 11);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  // Auto-preenche endereço quando o telefone chegar a 11 dígitos
  useEffect(() => {
    const digits = formData.phone.replace(/\D/g, '');
    if (digits.length !== 11 || !store?.id) return;
    const controller = new AbortController();
    fetch(`${Environment.API_URL}/clientes/endereco?storeId=${store.id}&telefone=${digits}`, { signal: controller.signal })
      .then(r => r.json())
      .then(data => {
        if (!data.endereco) return;
        const e = data.endereco;
        setFormData(prev => ({
          ...prev,
          name:        !prev.name.trim()        ? (e.nome       || prev.name)        : prev.name,
          address:     !prev.address.trim()     ? (e.logradouro || prev.address)     : prev.address,
          numero:      !prev.numero.trim()      ? (e.numero     || prev.numero)      : prev.numero,
          complemento: !prev.complemento.trim() ? (e.complemento|| prev.complemento) : prev.complemento,
          bairro:      !prev.bairro.trim()      ? (e.bairro     || prev.bairro)      : prev.bairro,
          reference:   !prev.reference.trim()   ? (e.referencia || prev.reference)   : prev.reference,
        }));
      })
      .catch(() => {});
    return () => controller.abort();
  }, [formData.phone, store?.id]);

  const saveEndereco = () => {
    if (deliveryType !== 'delivery' || !store?.id) return;
    const digits = formData.phone.replace(/\D/g, '');
    if (digits.length !== 11 || !formData.address.trim()) return;
    fetch(`${Environment.API_URL}/clientes/endereco`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        storeId:     store.id,
        telefone:    digits,
        nome:        formData.name,
        logradouro:  formData.address,
        numero:      formData.numero,
        complemento: formData.complemento,
        bairro:      formData.bairro,
        referencia:  formData.reference,
      }),
    }).catch(() => {});
  };

  const buildOrderItems = () => items.map((item: any) => {
    let additionalsTotal = 0;
    const qtys: Record<string, number> = item.options?.additionalQtys || {};
    if (Object.keys(qtys).length > 0) {
      Object.entries(qtys).forEach(([name, qty]: [string, any]) => {
        const a = (additionals || []).find((opt: any) => opt.name === name);
        if (a?.price) additionalsTotal += (a.price as number) * qty;
      });
    } else if (item.options?.additionals?.length) {
      (item.options.additionals as string[]).forEach((name: string) => {
        const a = (additionals || []).find((opt: any) => opt.name === name);
        if (a?.price) additionalsTotal += a.price as number;
      });
    }
    let itemPrice = item.product.price;
    if ((item.product as any).hasVariableSizePrice && item.options?.size) {
      const sz = ((item.product as any).sizes || []).find((s: any) => s.name === item.options.size);
      if (sz) itemPrice = sz.price;
    }
    return {
      id: item.product.id,
      name: item.product.name,
      qty: item.quantity,
      price: itemPrice,
      additionalsTotal,
      size: item.options?.size || null,
      sauces: item.options?.sauces || [],
      sauceQtys: item.options?.sauceQtys || {},
      sideDish: item.options?.sideDishes?.[0] || null,
      sideDishes: item.options?.sideDishes || [],
      flavors: item.options?.flavors || [],
      additionals: item.options?.additionals || [],
      salads: item.options?.salads || [],
      desserts: item.options?.desserts || [],
      removedIngredients: item.options?.removedIngredients || [],
      observations: item.options?.observations || '',
      isCombo: item.product.isCombo || false,
      comboItems: item.product.isCombo ? (item.product.comboItems || []) : undefined,
      comboSelections: item.product.isCombo ? (item.options?.comboSelections || {}) : undefined,
    };
  });

  const handleInitiatePayment = async () => {
    if (!validateForm()) return;

    try {
        setIsLoadingPayment(true);
        pendingWaMessage.current = formatWhatsAppMessage();

        const orderPayload = {
            storeId: store?.id || '',
            customerName: formData.name,
            phone: formData.phone.replace(/\D/g, ''),
            address: deliveryType === 'table' ? `Mesa ${formData.mesa}` : deliveryAddress,
            referencePoint: formData.reference,
            deliveryMode: (deliveryType === 'delivery' ? 'entrega' : deliveryType === 'table' ? 'comanda' : 'retirada') as any,
            payment: formData.paymentMethod === 'pix' ? 'PIX (Mercado Pago)' : 'Cartão (Mercado Pago)',
            items: buildOrderItems(),
            subtotal,
            deliveryFee,
            discountAmount,
            appliedCupom,
            promotionDiscountAmount: promotionDiscount,
            total: localTotal,
        };

        const cartStagingData = {
            customerName: formData.name,
            phone: formData.phone.replace(/\D/g, ''),
            items: orderPayload.items.map((i: any) => ({ id: i.id, name: i.name, qty: i.qty, price: i.price })),
            subtotal,
            deliveryFee,
            total: localTotal,
            deliveryMode: orderPayload.deliveryMode,
            address: orderPayload.address,
            payment: orderPayload.payment,
        };

        if (requirePaymentBeforeOrder && formData.paymentMethod === 'pix') {
            // PIX com exigência de pagamento antes: salva no abandoned cart como staging
            const abandonedCartId = await TrackingService.saveCartWithOrderData(store?.id || '', cartStagingData, orderPayload);
            if (!abandonedCartId) throw new Error('Falha ao criar staging do pedido');
            setCurrentAbandonedCartId(abandonedCartId);

            const pixResponse = await PaymentService.createPixPayment(
                store?.id || '',
                null,
                localTotal,
                { name: formData.name, phone: formData.phone.replace(/\D/g, '') },
                abandonedCartId
            );
            setPixData({
                paymentId: String(pixResponse.paymentId),
                qr_code: pixResponse.qr_code,
                qr_code_base64: pixResponse.qr_code_base64
            });
            setPaymentStep('pix');
            return;
        }

        if (formData.paymentMethod === 'card') {
            // Cartão automático: pré-pagamento — pedido só criado após aprovação
            const abandonedCartId = await TrackingService.saveCartWithOrderData(store?.id || '', cartStagingData, orderPayload);
            if (!abandonedCartId) throw new Error('Falha ao criar staging do pedido');
            setCurrentAbandonedCartId(abandonedCartId);
            setPaymentStep('payment');
            return;
        }

        // PIX fluxo normal: cria order imediatamente
        saveEndereco();
        const orderResponse = await OrderService.create(orderPayload);
        const orderId = orderResponse.order._id;
        setCurrentOrderId(orderId);
        if (orderResponse.order.orderNumber) pendingOrderNumber.current = orderResponse.order.orderNumber;

        const pixResponse = await PaymentService.createPixPayment(
            store?.id || '',
            orderId,
            localTotal,
            { name: formData.name, phone: formData.phone.replace(/\D/g, '') }
        );
        setPixData({
            paymentId: String(pixResponse.paymentId),
            qr_code: pixResponse.qr_code,
            qr_code_base64: pixResponse.qr_code_base64
        });
        setPaymentStep('pix');
    } catch (error: any) {
        showToast('Erro ao iniciar pagamento. Tente novamente.', 'error');
    } finally {
        setIsLoadingPayment(false);
    }
  };

  const handlePaymentSubmit = async (cardFormData: any, additionalData?: any) => {
      const waUrl = `https://wa.me/${(store?.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(pendingWaMessage.current)}`;
      const waWindow = isGestorMode ? null : window.open('about:blank', '_blank');
      try {
          setIsLoadingPayment(true);
          const result = await PaymentService.processCardPayment(
              store?.id || '',
              currentAbandonedCartId ? null : (currentOrderId || ''),
              cardFormData,
              additionalData,
              currentAbandonedCartId || undefined
          );
          if (result.status === 'approved') {
              if (isGestorMode) { finalizeGestor(); return; }
              const cardOrderNumber = result.orderNumber;
              const cardFinalUrl = cardOrderNumber
                ? `https://wa.me/${(store?.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(`*Pedido #${cardOrderNumber}*\n${pendingWaMessage.current}`)}`
                : waUrl;
              openWaMe(cardFinalUrl, waWindow);
              showToast('Pagamento aprovado!', 'success');
              clearCart();
              setPaymentStep('success');
          } else {
              waWindow?.close();
              if (result.status === 'in_process') showToast('Pagamento em análise. Aguarde a confirmação.', 'warning');
              else showToast('Pagamento não aprovado. Verifique os dados do cartão.', 'error');
          }
      } catch (error) {
          waWindow?.close();
          showToast('Erro ao processar pagamento. Tente novamente.', 'error');
      } finally {
          setIsLoadingPayment(false);
      }
  };

  useEffect(() => {
      if (paymentStep !== 'pix' || !pixData || !store?.id) return;

      const storeId = store.id;
      const orderId = currentOrderId;
      const cartId = currentAbandonedCartId;
      const paymentId = pixData.paymentId;

      const interval = setInterval(async () => {
          try {
              const { status } = await PaymentService.getPaymentStatus(storeId, paymentId);
              if (status === 'approved') {
                  clearInterval(interval);
                  // Confirmar no backend: atualiza DB e emite socket ao Gestor
                  try {
                      const confirmRes = await PaymentService.confirmPaymentFromClient(storeId, orderId, cartId, paymentId);
                      if (confirmRes?.orderNumber) pendingOrderNumber.current = confirmRes.orderNumber;
                  } catch (_) {}
                  showToast('Pagamento PIX confirmado!', 'success');
                  setPaymentStep('payment_confirmed');
              } else if (status === 'expired' || status === 'rejected') {
                  clearInterval(interval);
                  showToast('PIX expirado. Por favor, tente novamente.', 'error');
                  setPaymentStep('info');
              }
          } catch {}
      }, 5000);

      return () => clearInterval(interval);
  }, [paymentStep, pixData, store?.id, currentOrderId, currentAbandonedCartId]);

  const openWaMe = (url: string, preOpened?: Window | null) => {
    if (preOpened && !preOpened.closed) {
      preOpened.location.href = url;
    } else {
      // fallback: tenta window.open de novo, ou redireciona na mesma aba
      try { window.open(url, '_blank'); } catch { window.location.href = url; }
    }
  };

  const handleFinalizeOrder = async () => {
      if (isGestorMode) {
          if (currentOrderId) { try { await OrderService.clientFinalized(currentOrderId); } catch {} }
          finalizeGestor();
          return;
      }
      // Abrir janela sincronamente ANTES de qualquer await (Safari/Instagram exigem isso)
      const pixOrderNumber = pendingOrderNumber.current;
      const baseMessage = pendingWaMessage.current;
      const waUrl = pixOrderNumber
        ? `https://wa.me/${(store?.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(`*Pedido #${pixOrderNumber}*\n${baseMessage}`)}`
        : `https://wa.me/${(store?.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(baseMessage)}`;
      const waWindow = window.open('about:blank', '_blank');
      if (currentOrderId) {
          try { await OrderService.clientFinalized(currentOrderId); } catch {}
      }
      openWaMe(waUrl, waWindow);
      clearCart();
      setPaymentStep('success');
  };

  const handleClassicPixCheckout = async () => {
    try {
      setIsLoadingPayment(true);
      pendingWaMessage.current = formatWhatsAppMessage();
      const orderData = {
        storeId: store?.id || '',
        customerName: formData.name,
        phone: formData.phone.replace(/\D/g, ''),
        address: deliveryType === 'delivery' ? deliveryAddress : '',
        referencePoint: formData.reference,
        deliveryMode: (deliveryType === 'delivery' ? 'entrega' : 'retirada') as any,
        payment: 'PIX',
        changeValue: '',
        items: buildOrderItems(),
        subtotal,
        deliveryFee,
        discountAmount,
        appliedCupom,
        promotionDiscountAmount: promotionDiscount,
        total: localTotal,
      };
      const response = await OrderService.create(orderData);
      const orderId = response.order._id;
      setCurrentOrderId(orderId);
      if (response.order?.orderNumber) pendingOrderNumber.current = response.order.orderNumber;
      if (orderId && store?.id) await TrackingService.markCartConverted(orderId, store.id);
      saveEndereco();
      if (isGestorMode) { finalizeGestor(); return; }
      setPaymentStep('classic-pix');
    } catch {
      showToast('Erro ao registrar pedido. Tente novamente.', 'error');
    } finally {
      setIsLoadingPayment(false);
    }
  };

  const handleClassicPixWhatsApp = () => {
    if (isGestorMode) {
      if (currentOrderId) OrderService.clientFinalized(currentOrderId).catch(() => {});
      finalizeGestor();
      return;
    }
    const orderNumber = pendingOrderNumber.current;
    const baseMessage = pendingWaMessage.current;
    const phone = (store?.phone || '').replace(/\D/g, '');
    const waUrl = orderNumber
      ? `https://wa.me/${phone}?text=${encodeURIComponent(`*Pedido #${orderNumber}*\n${baseMessage}`)}`
      : `https://wa.me/${phone}?text=${encodeURIComponent(baseMessage)}`;
    if (currentOrderId) OrderService.clientFinalized(currentOrderId).catch(() => {});
    window.open(waUrl, '_blank');
    clearCart();
    setPaymentStep('success');
  };

  const validateForm = (): boolean => {
    for (const item of items) {
        const product = item.product;
        if ((product as any).hasVariableSizePrice && (product as any).sizes?.length > 0 && !item.options?.size) {
            showToast(`Por favor, selecione o tamanho para: ${product.name}`, 'warning');
            return false;
        }
        if (product.hasFlavor) {
            const opts = getProductFlavors(product);
            if (opts.length > 0 && (!item.options?.flavors || item.options.flavors.length === 0)) {
                showToast(`Por favor, selecione o sabor para: ${product.name}`, 'warning');
                return false;
            }
        }
        if (product.hasSauce && product.sauceRequired !== false && (product.sauceLimit || 0) > 0) {
            const sauceMin = (product as any).sauceMinimum || 1;
            if (!item.options?.sauces || item.options.sauces.length < sauceMin) {
                showToast(`Escolha ao menos ${sauceMin} molho${sauceMin > 1 ? 's' : ''} para: ${product.name}`, 'warning');
                return false;
            }
        }
        if (product.hasSideDish && (!item.options?.sideDishes || item.options.sideDishes.length === 0)) {
            const label = (product as any).sideDishAsPrepMode ? 'o modo de preparo' : 'o acompanhamento';
            showToast(`Por favor, selecione ${label} para: ${product.name}`, 'warning');
            return false;
        }
        if ((product as any).hasIncludedAdditionals) {
            const opts = getProductIncludedAdditionals(product);
            if (opts.length > 0) {
                const total = Object.values((item.options?.includedAdditionalQtys || {}) as Record<string, number>).reduce((s, q) => s + q, 0);
                if (total === 0) {
                    showToast(`Por favor, escolha os adicionais inclusos para: ${product.name}`, 'warning');
                    return false;
                }
            }
        }
    }
    const isActiveComanda = (deliveryType === 'comanda-simples' || deliveryType === 'comanda-cartao') && hasActiveComandaSession;
    if (!isActiveComanda && !formData.name.trim()) { showToast('Por favor, preencha seu nome', 'error'); return false; }
    if (!isActiveComanda && formData.phone.replace(/\D/g, '').length !== 11) { showToast('Por favor, preencha um telefone válido', 'error'); return false; }
    if (deliveryType === 'delivery' && !formData.address.trim()) { showToast('Por favor, preencha o logradouro de entrega', 'error'); return false; }
    if (deliveryType === 'delivery' && !formData.numero.trim()) { showToast('Por favor, preencha o número da residência', 'error'); return false; }
    if (deliveryType === 'delivery' && !formData.bairro.trim()) { showToast('Por favor, informe o bairro', 'error'); return false; }
    // Validação fiscal
    const isFiscalMode = !isAutomated && !!(store as any)?.paymentMethodsReady && visibleFiscalMethods.length > 0
      && deliveryType !== 'table' && deliveryType !== 'comanda-simples' && deliveryType !== 'comanda-cartao';
    if (isFiscalMode && !selectedFiscalPM) { showToast('Por favor, selecione a forma de pagamento', 'error'); return false; }
    if (isFiscalMode && selectedFiscalPM?.variacoes?.length > 0 && selectedVariacaoIdx < 0) {
      showToast(`Por favor, selecione a bandeira para ${selectedFiscalPM.nome}`, 'error'); return false;
    }
    return true;
  };

  const extraCosts = useMemo(() => {
    return items.reduce((acc: number, item: any) => {
      let itemExtras = 0;
      const product = item.product;
      
      if (item.options?.flavors?.length) {
        const flavorsData = getProductFlavors(product);
        item.options.flavors.forEach((name: string) => {
          const f = flavorsData.find((opt: any) => opt.name === name);
          if (f?.price) itemExtras += f.price;
        });
      }
      
      if (getExtrasChargeAdditionals(product)) {
        if (item.options?.additionalQtys) {
          const additionalsData = getProductAdditionals(product);
          Object.entries(item.options.additionalQtys as Record<string, number>).forEach(([name, qty]) => {
            const a = additionalsData.find((opt: any) => opt.name === name);
            if (a?.price) itemExtras += a.price * qty;
          });
        } else if (item.options?.additionals?.length) {
          const additionalsData = getProductAdditionals(product);
          (item.options.additionals as string[]).forEach((name: string) => {
            const a = additionalsData.find((opt: any) => opt.name === name);
            if (a?.price) itemExtras += a.price;
          });
        }
      }

      if (item.options?.sideDishes?.length && !(product as any).hasVariablePrice) {
        const sidesData = getProductSideDishes(product);
        item.options.sideDishes.forEach((name: string) => {
          const s = sidesData.find((opt: any) => opt.name === name);
          if (s?.price) itemExtras += s.price;
        });
      }

      if (item.options?.sauces?.length) {
        const saucesData = getProductSauces(product);
        if ((product as any).sauceAllowMultiple && item.options?.sauceQtys) {
          const qtys = item.options.sauceQtys as Record<string, number>;
          Object.entries(qtys).forEach(([name, qty]) => {
            const s = saucesData.find((opt: any) => opt.name === name);
            if (s?.price && qty > 0) itemExtras += s.price * qty;
          });
        } else {
          item.options.sauces.forEach((name: string) => {
            const s = saucesData.find((opt: any) => opt.name === name);
            if (s?.price) itemExtras += s.price;
          });
        }
      }

      return acc + (itemExtras * item.quantity);
    }, 0);
  }, [items, getProductFlavors, getProductAdditionals, getProductSideDishes, getProductSauces]);

  // Efeito para salvar carrinho abandonado quando nome e telefone forem preenchidos
  useEffect(() => {
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (formData.name.trim().length > 3 && phoneDigits.length === 11 && items.length > 0 && store?.id) {
        const cartData = {
            customerName: formData.name,
            phone: phoneDigits,
            items: items.map((item: any) => ({
                id: item.product.id,
                name: item.product.name,
                qty: item.quantity,
                price: item.product.price,
                options: item.options
            })),
            subtotal,
            total: localTotal,
            deliveryFee
        };

        // Debounce simples para não sobrecarregar a API enquanto o usuário digita
        const timer = setTimeout(() => {
            TrackingService.saveAbandonedCart(store.id, cartData);
        }, 2000);

        return () => clearTimeout(timer);
    }
  }, [formData.name, formData.phone, items, store?.id, subtotal, localTotal, deliveryFee]);

  const formatWhatsAppMessage = (): string => {
    let message = `🛒 *NOVO PEDIDO*\n\n👤 *Cliente:* ${formData.name}\n📱 *Telefone:* ${formData.phone}\n\n📦 *Itens do Pedido:*\n`;
    items.forEach((item: any, index: number) => {
      let itemLinePrice = item.product.price;
      if ((item.product as any).hasVariableSizePrice && item.options?.size) {
        const sz = ((item.product as any).sizes || []).find((s: any) => s.name === item.options.size);
        if (sz) itemLinePrice = sz.price;
      }
      message += `\n${index + 1}. *${item.product.name}* (${item.quantity}x)\n   💰 R$ ${itemLinePrice.toFixed(2)}\n`;
      const opt = item.options;
      if (opt) {
        if (opt.comboSelections) {
          (item.product.comboItems || []).forEach((ci: any) => {
            for (let slotIdx = 0; slotIdx < ci.quantity; slotIdx++) {
              const slotKey = `${ci.productId}_${slotIdx}`;
              const sel = opt.comboSelections[slotKey] || {};
              const label = ci.quantity > 1 ? `${ci.productName} ${slotIdx + 1}` : ci.productName;
              message += `   🍱 ${label}`;
              const details: string[] = [];
              if (sel.flavors?.length) details.push(`Sabor: ${sel.flavors.join(', ')}`);
              if (sel.sauces?.length) details.push(`Molho: ${sel.sauces.join(', ')}`);
              if (sel.sideDishes?.length) details.push(`Acomp.: ${sel.sideDishes.join(', ')}`);
              const addEntries = Object.entries(sel.additionalQtys || {}).filter(([, q]) => (q as number) > 0);
              if (addEntries.length) details.push(`Adicionais: ${addEntries.map(([n, q]) => (q as number) > 1 ? `${n} (${q}x)` : n).join(', ')}`);
              if (details.length) message += ` — ${details.join(' | ')}`;
              message += '\n';
            }
          });
        } else {
          if (opt.size) message += `   📐 Tamanho: ${opt.size}\n`;
          if (opt.flavors?.length) message += `   🍕 Sabores: ${opt.flavors.join(', ')}\n`;
          if (opt.includedAdditionalQtys) {
            const incLines = Object.entries(opt.includedAdditionalQtys as Record<string,number>)
              .map(([n, q]) => q > 1 ? `${n} (${q}x)` : n).join(', ');
            if (incLines) message += `   ✅ Inclusos (grátis): ${incLines}\n`;
          } else if (opt.includedAdditionals?.length) message += `   ✅ Inclusos (grátis): ${opt.includedAdditionals.join(', ')}\n`;
          if (opt.additionalQtys) {
            const addLines = Object.entries(opt.additionalQtys as Record<string,number>)
              .map(([n, q]) => q > 1 ? `${n} (${q}x)` : n).join(', ');
            if (addLines) message += `   ➕ Adicionais Extras: ${addLines}\n`;
          } else if (opt.additionals?.length) message += `   ➕ Adicionais Extras: ${opt.additionals.join(', ')}\n`;
          if (opt.sauces?.length) message += `   🍯 Molhos: ${opt.sauces.map((s: string) => { const q = (opt.sauceQtys as any)?.[s]; return q && q > 1 ? `${s} (${q}x)` : s; }).join(', ')}\n`;
          if (opt.sideDishes?.length) message += `   🍟 Acompanhamentos: ${opt.sideDishes.join(', ')}\n`;
          if (opt.salads?.length) message += `   🥗 Saladas: ${opt.salads.join(', ')}\n`;
          if (opt.desserts?.length) message += `   🍰 Sobremesas: ${opt.desserts.join(', ')}\n`;
          if (opt.removedIngredients?.length) message += `   🚫 Retirar: ${opt.removedIngredients.join(', ')}\n`;
          if (opt.observations?.trim()) message += `   📝 Obs: ${opt.observations.trim()}\n`;
        }
      }
    });
    message += `\n💵 *Subtotal:* R$ ${subtotal.toFixed(2)}\n`;
    if (extraCosts > 0) message += `➕ *Adicionais:* R$ ${extraCosts.toFixed(2)}\n`;
    if (promotionDiscount > 0) message += `🏷️ *Desconto Promoção:* -R$ ${promotionDiscount.toFixed(2)}\n`;
    if (appliedCupom) message += `🎟️ *Cupom:* ${appliedCupom} (-R$ ${discountAmount.toFixed(2)})\n`;
    if (deliveryType === 'delivery') {
      message += `🚚 *Taxa de Entrega:* R$ ${deliveryFee.toFixed(2)}\n📍 *Endereço:* ${deliveryAddress}\n`;
      if (formData.reference) message += `🏠 *Referência:* ${formData.reference}\n`;
    } else if (deliveryType === 'table') {
      message += `🍽️ *Mesa:* ${formData.mesa}\n🔗 *Acompanhar Comanda:* ${window.location.origin}/comanda?telefone=${formData.phone.replace(/\D/g, '')}&senha=${formData.password}\n`;
    } else message += `🏪 *Retirada no Local*\n📍 *Endereço da Loja:* ${store?.address || 'Não cadastrado'}\n`;
    let paymentLabel = formData.paymentMethod === 'pix' ? 'PIX' : formData.paymentMethod === 'card' ? 'Cartão' : 'Dinheiro';
    if (selectedFiscalPM) {
      paymentLabel = selectedFiscalPM.nome;
      if (selectedVariacaoIdx >= 0 && (selectedFiscalPM.variacoes || []).length > 0) {
        paymentLabel += ` — ${variacaoNome(selectedFiscalPM.variacoes[selectedVariacaoIdx])}`;
      }
    }
    message += `\n💳 *Forma de Pagamento:* ${paymentLabel}`;
    if (paymentFee > 0) message += ` (+R$ ${paymentFee.toFixed(2)} taxa)`;
    if (formData.changeFor && parseFloat(formData.changeFor) > 0) message += ` (Troco para R$ ${parseFloat(formData.changeFor).toFixed(2)})`;
    if (!selectedFiscalPM && formData.paymentMethod === 'pix' && (store as any)?.pixKey) message += `\n🔑 *Chave PIX:* ${(store as any).pixKey}`;
    message += `\n\n✅ *TOTAL: R$ ${localTotal.toFixed(2)}*`;
    return message;
  };

  const handleComandaCheckout = async () => {
    const orderData = {
      storeId: store?.id || '',
      customerName: formData.name?.trim() || 'Cliente',
      phone: formData.phone.replace(/\D/g, ''),
      address: '',
      referencePoint: '',
      deliveryMode: 'comanda' as any,
      payment: 'comanda',
      changeValue: '',
      items: buildOrderItems(),
      subtotal,
      deliveryFee: 0,
      discountAmount,
      appliedCupom,
      promotionDiscountAmount: promotionDiscount,
      total: localTotal,
    };

    try {
      if (deliveryType === 'comanda-simples') {
        if (!store?.id || !comandaMesaNumber) return;

        // Se o cliente já tem uma comanda desta sessão, adiciona diretamente sem criar nova
        const sessionId = sessionStorage.getItem('comanda_simples_id');
        const sessionSenha = sessionStorage.getItem('comanda_simples_senha');

        if (sessionId && sessionSenha) {
          try {
            await OrderService.addOrderToComanda(sessionId, orderData);
            showToast('Pedido adicionado à comanda!', 'success');
            clearCart();
            onClose();
            navigate(`/comanda?id=${sessionId}&senha=${sessionSenha}`);
            return;
          } catch {
            // Comanda pode ter sido fechada — limpa sessão e cria nova
            sessionStorage.removeItem('comanda_simples_id');
            sessionStorage.removeItem('comanda_simples_senha');
          }
        }

        const status = await OrderService.getMesaStatus(store.id, comandaMesaNumber);
        if (status.ocupada) {
          showToast('Mesa já possui uma comanda ativa. Chame o garçom.', 'error');
          return;
        }

        const res = await OrderService.simplesAccess({
          storeId: store.id,
          mesaNumber: Number(comandaMesaNumber),
          customerName: orderData.customerName,
          phone: orderData.phone,
        });

        if (!res.success) { showToast('Erro ao criar comanda.', 'error'); return; }

        await OrderService.addOrderToComanda(res.comanda._id, orderData);
        sessionStorage.setItem('comanda_simples_id', res.comanda._id);
        sessionStorage.setItem('comanda_simples_senha', res.senha);
        if (formData.name.trim()) sessionStorage.setItem('comanda_name', formData.name.trim());
        setComandaId(res.comanda._id);
        setHasActiveComandaSession(true);
        showToast('Pedido registrado na comanda!', 'success');
        clearCart();
        onClose();
        navigate(`/comanda?id=${res.comanda._id}&senha=${res.senha}`);

      } else if (deliveryType === 'comanda-cartao') {
        if (!store?.id || !cartaoCode) return;

        const res = await OrderService.cartaoAccess({
          storeId: store.id,
          cartaoCode,
          customerName: orderData.customerName,
          phone: orderData.phone,
        });

        if (!res.success) { showToast('Erro ao acessar cartão.', 'error'); return; }

        await OrderService.addOrderToComanda(res.comanda._id, orderData);
        if (cartaoCode) sessionStorage.setItem(`comanda_cartao_${cartaoCode}`, res.comanda._id);
        setHasActiveComandaSession(true);
        showToast('Pedido registrado na comanda!', 'success');
        clearCart();
        onClose();
        navigate(`/comanda?cartao=${cartaoCode}`);
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Erro ao processar comanda.';
      showToast(msg, 'error');
    }
  };

  const handleCheckout = async () => {
    if (!validateForm()) return;

    // Fluxos de comanda via QR Code (simples ou cartão)
    if (deliveryType === 'comanda-simples' || deliveryType === 'comanda-cartao') {
      await handleComandaCheckout();
      return;
    }

    // Se for automatizado, não for comanda, não for dinheiro e não for cartão clássico, vai para o fluxo de pagamento
    if (shouldUseAutomatedFlow) {
        await handleInitiatePayment();
        return;
    }

    // PIX clássico: cria pedido primeiro, mostra chave PIX depois
    if (isClassicPix) {
        await handleClassicPixCheckout();
        return;
    }

    const orderPaymentLabel = (() => {
      if (selectedFiscalPM) {
        let p = selectedFiscalPM.nome;
        if (selectedVariacaoIdx >= 0 && (selectedFiscalPM.variacoes || []).length > 0) p += ` — ${variacaoNome(selectedFiscalPM.variacoes[selectedVariacaoIdx])}`;
        return p;
      }
      return formData.paymentMethod === 'pix' ? 'PIX' : formData.paymentMethod === 'card' ? 'Cartão' : 'Dinheiro';
    })();

    const orderData = {
      storeId: store?.id || '', customerName: formData.name, phone: formData.phone.replace(/\D/g, ''),
      address: deliveryType === 'table' ? `Mesa ${formData.mesa}` : deliveryAddress, referencePoint: formData.reference,
      deliveryMode: (deliveryType === 'delivery' ? 'entrega' : deliveryType === 'table' ? 'comanda' : 'retirada') as any,
      payment: orderPaymentLabel,
      paymentVariacao: selectedFiscalPM && selectedVariacaoIdx >= 0 && (selectedFiscalPM.variacoes || []).length > 0
        ? variacaoNome(selectedFiscalPM.variacoes[selectedVariacaoIdx]) : '',
      paymentRefFiscal: selectedFiscalPM?.refFiscal || '',
      changeValue: formData.changeFor || '', items: buildOrderItems(), subtotal, deliveryFee, discountAmount, appliedCupom, promotionDiscountAmount: promotionDiscount, total: localTotal,
    };

    if (isGestorMode) {
      // Modo gestor: cria pedido, limpa carrinho e recarrega — sem WhatsApp
      try {
        if (deliveryType === 'table') {
          showToast('Preencha os dados da mesa e senha', 'error'); return;
        }
        const response = await OrderService.create(orderData);
        if (response.order?._id && store?.id) await TrackingService.markCartConverted(response.order._id, store.id);
        saveEndereco();
        finalizeGestor();
      } catch { showToast('Erro ao enviar pedido.', 'error'); }
      return;
    }

    // Abrir janela sincronamente ANTES de qualquer await — Safari e Instagram só permitem
    // window.open dentro do gesto do usuário. Após um await o contexto é perdido.
    const message = formatWhatsAppMessage();
    const waUrl = `https://wa.me/${(store?.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
    const waWindow = window.open('about:blank', '_blank');

    try {
      if (deliveryType === 'table' && (!formData.mesa || !formData.password)) {
        waWindow?.close();
        showToast('Preencha os dados da mesa e senha', 'error');
        return;
      }
      if (deliveryType === 'table') {
        const accessRes = await OrderService.accessComanda({ storeId: store?.id || '', phone: orderData.phone, mesaNumber: parseInt(formData.mesa || '0'), customerName: orderData.customerName, password: formData.password });
        if (accessRes.success) {
          await OrderService.addOrderToComanda(accessRes.comanda._id, orderData);
          sessionStorage.setItem('comanda_name', formData.name); sessionStorage.setItem('comanda_phone', orderData.phone); sessionStorage.setItem('comanda_mesa', formData.mesa || ''); sessionStorage.setItem('comanda_password', formData.password || '');
          showToast('Pedido adicionado à comanda!', 'success'); clearCart(); onClose();
          if (accessRes.comanda.orders.length === 0) openWaMe(waUrl, waWindow);
          else waWindow?.close();
          navigate(`/comanda?telefone=${orderData.phone}&senha=${formData.password}`); return;
        }
      }
      const response = await OrderService.create(orderData);
      if (response.order?._id && store?.id) await TrackingService.markCartConverted(response.order._id, store.id);
      saveEndereco();
      const orderNumber = response.order?.orderNumber;
      const finalUrl = orderNumber
        ? `https://wa.me/${(store?.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(`*Pedido #${orderNumber}*\n${message}`)}`
        : waUrl;
      openWaMe(finalUrl, waWindow);
      showToast('Pedido enviado!', 'success'); clearCart(); onClose();
    } catch (error) {
      waWindow?.close();
      showToast('Erro ao enviar pedido.', 'error');
    }
  };

  const getProductIngredients = useCallback((product: any) => {
    const canEdit = product.hasIngredient || product.allowCustomerEditIngredient;
    if (!canEdit) return [];
    if (product.hasProductIngredients && product.ingredients?.length) return (allIngredients || []).filter((i: any) => product.ingredients.includes(i.id || i._id));
    if (product.ingredientListId) {
      const list = (ingredientLists || []).find((l: any) => l.id === product.ingredientListId || l._id === product.ingredientListId);
      if (list) {
        const itemIds = list.ingredients.map((i: any) => typeof i === 'object' ? (i.id || i._id) : i);
        return (allIngredients || []).filter((i: any) => itemIds.includes(i.id || i._id));
      }
    }
    return allIngredients || [];
  }, [allIngredients, ingredientLists]);

  const ingredientConfirmModal = ingredientConfirm && (
    <div style={{position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px'}} onClick={() => setIngredientConfirm(null)}>
      <div style={{background:'white', borderRadius:'16px', padding:'24px', maxWidth:'320px', width:'100%'}} onClick={e => e.stopPropagation()}>
        <div style={{fontSize:'1rem', fontWeight:700, marginBottom:'8px', color:'#0f172a'}}>Remover ingrediente?</div>
        <div style={{fontSize:'0.9rem', color:'#64748b', marginBottom:'20px'}}>
          Deseja retirar <strong>{ingredientConfirm.ingredient}</strong> do seu pedido?
        </div>
        <div style={{display:'flex', gap:'10px'}}>
          <button onClick={() => setIngredientConfirm(null)} style={{flex:1, padding:'12px', borderRadius:'10px', border:'1px solid #e2e8f0', background:'white', color:'#64748b', fontWeight:700, cursor:'pointer', fontSize:'0.9rem'}}>
            Cancelar
          </button>
          <button onClick={() => { toggleIngredient(ingredientConfirm.itemId, ingredientConfirm.ingredient); setIngredientConfirm(null); }} style={{flex:1, padding:'12px', borderRadius:'10px', border:'none', background:'#ef4444', color:'white', fontWeight:700, cursor:'pointer', fontSize:'0.9rem'}}>
            Retirar
          </button>
        </div>
      </div>
    </div>
  );

  const modalBody = (
    <>
      {ingredientConfirmModal}
      {showBandeirasModal && (
        <div style={{position:'fixed', inset:0, zIndex:99999, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px'}} onClick={() => setShowBandeirasModal(false)}>
          <div style={{background:'white', borderRadius:'16px', padding:'24px', maxWidth:'320px', width:'100%'}} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px'}}>
              <div style={{fontWeight:700, fontSize:'1rem', color:'#0f172a'}}>Bandeiras Aceitas</div>
              <button onClick={() => setShowBandeirasModal(false)} style={{background:'none', border:'none', cursor:'pointer', color:'#64748b', fontSize:'1.3rem', lineHeight:1, padding:'0 4px'}}>✕</button>
            </div>
            <div style={{display:'flex', flexWrap:'wrap', gap:'8px'}}>
              {cardBrands.map(brand => (
                <span key={brand} style={{background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:'8px', padding:'6px 12px', fontSize:'0.875rem', fontWeight:600, color:'#374151'}}>{brand}</span>
              ))}
            </div>
          </div>
        </div>
      )}
      {paymentStep === 'info' && (
        <>
          {items.length === 0 ? (
            <CartEmpty>
              <EmptyIcon>
                <i className="fas fa-shopping-basket" style={{ fontSize: '3rem', opacity: 0.2 }}></i>
              </EmptyIcon>
              <EmptyText>Seu carrinho está vazio</EmptyText>
            </CartEmpty>
          ) : (
            <>
              <CartItems>
                {items.map((item: any) => (
                  <CartItem key={item.id}>
                    <DuplicateButton title="Duplicar Item" onClick={() => duplicateItem(item.id)}>
                      <i className="fas fa-copy"></i>
                    </DuplicateButton>
                    <RemoveButton title="Remover Item" onClick={() => removeItem(item.id)}>
                      <i className="fas fa-trash-alt"></i>
                    </RemoveButton>
                    <ItemHeader>
                      {item.product.image && <CartItemImage imageId={item.product.image} alt={item.product.name} />}
                      <ItemInfo>
                        <ItemName>{item.product.name}</ItemName>
                        {(item.product as any).hasVariableSizePrice ? (
                          (() => {
                            const sizeName = item.options?.size;
                            const sizePrice = sizeName
                              ? (((item.product as any).sizes || []).find((s: any) => s.name === sizeName)?.price ?? 0)
                              : 0;
                            return sizeName ? <ItemPrice>R$ {(sizePrice * item.quantity).toFixed(2)}</ItemPrice> : null;
                          })()
                        ) : (item.product as any).hasVariablePrice ? (
                          (() => {
                            const sdName = item.options?.sideDishes?.[0];
                            const sdPrice = sdName
                              ? (getProductSideDishes(item.product).find((s: any) => s.name === sdName)?.price ?? 0)
                              : 0;
                            return sdName ? <ItemPrice>R$ {(sdPrice * item.quantity).toFixed(2)}</ItemPrice> : null;
                          })()
                        ) : (
                          <ItemPrice>R$ {(item.product.price * item.quantity).toFixed(2)}</ItemPrice>
                        )}
                        <QuantityControl>
                          <QuantityButton onClick={() => updateQuantity(item.id, item.quantity - 1)}>
                            <i className="fas fa-minus"></i>
                          </QuantityButton>
                          <Quantity>{item.quantity}</Quantity>
                          <QuantityButton onClick={() => updateQuantity(item.id, item.quantity + 1)}>
                            <i className="fas fa-plus"></i>
                          </QuantityButton>
                        </QuantityControl>
                      </ItemInfo>
                    </ItemHeader>

                    {/* Combo: per-slot option sections (one per sub-item × quantity) */}
                    {item.product.isCombo && (item.product.comboItems || []).flatMap((ci: any) =>
                      Array.from({ length: ci.quantity }, (_, slotIdx) => {
                        const slotKey = `${ci.productId}_${slotIdx}`;
                        const subProduct = (allProducts || products || []).find((p: any) => String(p.id || p._id) === String(ci.productId));
                        if (!subProduct) return null;
                        const sel = getComboSlot(item, slotKey);
                        const subFlavors = getProductFlavors(subProduct);
                        const subSauces = getProductSauces(subProduct);
                        const subSideDishes = getProductSideDishes(subProduct);
                        const subAdditionals = getProductAdditionals(subProduct);
                        const label = ci.quantity > 1 ? `${ci.productName} ${slotIdx + 1}` : ci.productName;

                        const classMap: Record<string, any> = {};
                        (flavorClassifications || []).forEach((c: any) => { classMap[c.id || c._id] = c; });
                        const flavorGroups: Record<string, { classification: any; flavors: any[] }> = {};
                        const ungroupedFlavors: any[] = [];
                        subFlavors.forEach((f: any) => {
                          const cid = f.classificationId?._id || f.classificationId;
                          if (cid && classMap[cid]) {
                            if (!flavorGroups[cid]) flavorGroups[cid] = { classification: classMap[cid], flavors: [] };
                            flavorGroups[cid].flavors.push(f);
                          } else { ungroupedFlavors.push(f); }
                        });
                        const sortedFlavorGroups = Object.values(flavorGroups).sort((a: any, b: any) => (a.classification.order || 0) - (b.classification.order || 0));
                        if (ungroupedFlavors.length) sortedFlavorGroups.push({ classification: null, flavors: ungroupedFlavors });
                        const hasFlavorClassifications = subFlavors.some((f: any) => f.classificationId);

                        return (
                          <div key={slotKey} style={{ borderTop: '1px solid #ede9fe', paddingTop: '8px' }}>
                            <OptionLabel style={{ color: '#7c3aed', padding: '4px 0 6px' }}>{label}</OptionLabel>

                            {subFlavors.length > 0 && (
                              <CollapsibleItemOptions
                                label={<>{subProduct.flavorLimit ? `Sabor (até ${subProduct.flavorLimit})` : 'Sabor'} <span style={{color:'#ef4444', fontSize:'0.72rem'}}>* Obrigatório</span></>}
                                closeWhen={(sel.flavors || []).length >= (subProduct.flavorLimit || 1)}
                              >
                                {!hasFlavorClassifications ? (
                                  <OptionTable>
                                    {subFlavors.map((f: any) => {
                                      const isActive = (sel.flavors || []).includes(f.name);
                                      return (
                                        <OptionRow key={f.id || f._id} $active={isActive} onClick={() => toggleComboFlavor(item.id, slotKey, f.name, subProduct.flavorLimit || 1)}>
                                          <OptionCheckbox $active={isActive}>{isActive && <span style={{color:'white', fontSize:'10px', fontWeight:700, lineHeight:1}}>✓</span>}</OptionCheckbox>
                                          <OptionRowInfo>
                                            <OptionRowName $active={isActive}>{f.name}</OptionRowName>
                                            {f.description && <OptionRowDesc>{f.description}</OptionRowDesc>}
                                          </OptionRowInfo>
                                          {f.price > 0 && <OptionRowPrice>+R$ {f.price.toFixed(2)}</OptionRowPrice>}
                                        </OptionRow>
                                      );
                                    })}
                                  </OptionTable>
                                ) : (
                                  <>
                                    {sortedFlavorGroups.map((group: any, gi: number) => (
                                      <div key={group.classification?.id || gi}>
                                        {group.classification && (
                                          <div style={{ padding: '0.5rem 0.75rem 0.25rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            {group.classification.name}
                                          </div>
                                        )}
                                        <OptionTable>
                                          {group.flavors.map((f: any) => {
                                            const isActive = (sel.flavors || []).includes(f.name);
                                            return (
                                              <OptionRow key={f.id || f._id} $active={isActive} onClick={() => toggleComboFlavor(item.id, slotKey, f.name, subProduct.flavorLimit || 1)}>
                                                <OptionCheckbox $active={isActive}>{isActive && <span style={{color:'white', fontSize:'10px', fontWeight:700, lineHeight:1}}>✓</span>}</OptionCheckbox>
                                                <OptionRowInfo>
                                                  <OptionRowName $active={isActive}>{f.name}</OptionRowName>
                                                  {f.description && <OptionRowDesc>{f.description}</OptionRowDesc>}
                                                </OptionRowInfo>
                                                {f.price > 0 && <OptionRowPrice>+R$ {f.price.toFixed(2)}</OptionRowPrice>}
                                              </OptionRow>
                                            );
                                          })}
                                        </OptionTable>
                                      </div>
                                    ))}
                                  </>
                                )}
                              </CollapsibleItemOptions>
                            )}

                            {subSauces.length > 0 && (
                              <CollapsibleItemOptions
                                label={<>{subProduct.sauceLimit ? `Molho (até ${subProduct.sauceLimit})` : 'Molho'} <span style={{color:'#ef4444', fontSize:'0.72rem'}}>* Obrigatório</span></>}
                                closeWhen={(sel.sauces || []).length >= (subProduct.sauceLimit || 1)}
                              >
                                <OptionTable>
                                  {subSauces.map((s: any) => {
                                    const isActive = (sel.sauces || []).includes(s.name);
                                    return (
                                      <OptionRow key={s.id || s._id} $active={isActive} onClick={() => toggleComboSauce(item.id, slotKey, s.name, subProduct.sauceLimit || 1)}>
                                        <OptionCheckbox $active={isActive}>{isActive && <span style={{color:'white', fontSize:'10px', fontWeight:700, lineHeight:1}}>✓</span>}</OptionCheckbox>
                                        <OptionRowInfo><OptionRowName $active={isActive}>{s.name}</OptionRowName></OptionRowInfo>
                                        {s.price > 0 && <OptionRowPrice>+R$ {s.price.toFixed(2)}</OptionRowPrice>}
                                      </OptionRow>
                                    );
                                  })}
                                </OptionTable>
                              </CollapsibleItemOptions>
                            )}

                            {subSideDishes.length > 0 && (
                              <CollapsibleItemOptions
                                label={<>
                                  {(subProduct as any).sideDishAsPrepMode
                                    ? (subProduct.sideDishLimit ? `Modo de Preparo (até ${subProduct.sideDishLimit})` : 'Modo de Preparo')
                                    : (subProduct.sideDishLimit ? `Acompanhamento (até ${subProduct.sideDishLimit})` : 'Acompanhamento')
                                  }
                                  {' '}<span style={{color:'#ef4444', fontSize:'0.72rem'}}>* Obrigatório</span>
                                </>}
                                closeWhen={(sel.sideDishes || []).length >= (subProduct.sideDishLimit || 1)}
                              >
                                <OptionTable>
                                  {subSideDishes.map((s: any) => {
                                    const isActive = (sel.sideDishes || []).includes(s.name);
                                    return (
                                      <OptionRow key={s.id || s._id} $active={isActive} onClick={() => toggleComboSideDish(item.id, slotKey, s.name, subProduct.sideDishLimit || 1)}>
                                        <OptionCheckbox $active={isActive}>{isActive && <span style={{color:'white', fontSize:'10px', fontWeight:700, lineHeight:1}}>✓</span>}</OptionCheckbox>
                                        <OptionRowInfo><OptionRowName $active={isActive}>{s.name}</OptionRowName></OptionRowInfo>
                                        {s.price > 0 && <OptionRowPrice>+R$ {s.price.toFixed(2)}</OptionRowPrice>}
                                      </OptionRow>
                                    );
                                  })}
                                </OptionTable>
                              </CollapsibleItemOptions>
                            )}

                            {subAdditionals.length > 0 && (
                              <CollapsibleItemOptions
                                label={<>{subProduct.additionalLimit ? `Adicionais (até ${subProduct.additionalLimit})` : 'Adicionais'} <span style={{color:'#6b7280', fontSize:'0.72rem'}}>(Opcional)</span></>}
                              >
                                <OptionTable>
                                  {subAdditionals.map((a: any) => {
                                    const qty = ((sel.additionalQtys || {}) as Record<string, number>)[a.name] || 0;
                                    return (
                                      <div key={a.id || a._id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderBottom: '1px dashed #e2e8f0', background: qty > 0 ? 'rgba(124,58,237,0.06)' : 'white' }}>
                                        <OptionRowInfo>
                                          <OptionRowName $active={qty > 0}>{a.name}{a.price > 0 && <span style={{ color: '#10b981', marginLeft: 4 }}>+R${a.price.toFixed(2)}</span>}</OptionRowName>
                                        </OptionRowInfo>
                                        {qty === 0 ? (
                                          <button onClick={() => changeComboAdditionalQty(item.id, slotKey, a.name, 1, subProduct.additionalLimit || 0)} style={{ padding: '4px 10px', borderRadius: '6px', background: '#f5f3ff', border: '1px solid #c4b5fd', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, color: '#7c3aed', flexShrink: 0 }}>+ Adicionar</button>
                                        ) : (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                            <button onClick={() => changeComboAdditionalQty(item.id, slotKey, a.name, -1, subProduct.additionalLimit || 0)} style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#ea580c', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 700 }}>−</button>
                                            <span style={{ fontWeight: 700, minWidth: '16px', textAlign: 'center' }}>{qty}</span>
                                            <button onClick={() => changeComboAdditionalQty(item.id, slotKey, a.name, 1, subProduct.additionalLimit || 0)} style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#10b981', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 700 }}>+</button>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </OptionTable>
                              </CollapsibleItemOptions>
                            )}
                          </div>
                        );
                      })
                    )}

                    {!item.product.isCombo && (item.product as any).hasVariableSizePrice && (item.product as any).sizes?.length > 0 && (
                      <ItemOptions>
                        <OptionLabel>Tamanho <span style={{color: '#ef4444'}}>* Obrigatório</span></OptionLabel>
                        <OptionsGroup>
                          {((item.product as any).sizes || []).map((s: any) => (
                            <OptionChip
                              key={s.name}
                              $active={item.options?.size === s.name}
                              onClick={() => updateQuantity(item.id, item.quantity, { ...item.options, size: s.name })}
                            >
                              {s.name}{s.description ? ` — ${s.description}` : ''} (R$ {Number(s.price).toFixed(2)})
                            </OptionChip>
                          ))}
                        </OptionsGroup>
                      </ItemOptions>
                    )}

                    {!item.product.isCombo && item.product.hasSauce && (() => {
                      const isRequired = (item.product as any).sauceRequired !== false;
                      const allowMultiple = !!(item.product as any).sauceAllowMultiple;
                      const totalQty = allowMultiple
                        ? Object.values((item.options?.sauceQtys || {}) as Record<string, number>).reduce((s, q) => s + q, 0)
                        : (item.options?.sauces?.length || 0);
                      const limit = item.product.sauceLimit || 1;
                      return (
                        <CollapsibleItemOptions
                          label={(() => {
                            const sMin = (item.product as any).sauceMinimum || 1;
                            const sMax = item.product.sauceLimit || 0;
                            if (sMax === 0) return 'Escolha o Molho (opcional)';
                            if (!isRequired) return `Escolha até ${sMax} Molho${sMax > 1 ? 's' : ''}.`;
                            const txt = sMin === sMax ? `Escolha ${sMin} Molho${sMin > 1 ? 's' : ''}` : `Escolha de ${sMin} a ${sMax} Molho${sMax > 1 ? 's' : ''}`;
                            return <>{txt}{' '}<span style={{color: '#ef4444'}}>* Obrigatório</span></>;
                          })()}
                          closeWhen={totalQty >= limit}
                        >
                          <OptionTable>
                            {getProductSauces(item.product).map((s: any) => {
                              if (allowMultiple) {
                                const qty = ((item.options?.sauceQtys || {}) as Record<string, number>)[s.name] || 0;
                                return (
                                  <OptionRow key={s.id || s._id} $active={qty > 0} style={{cursor: 'default'}}>
                                    <OptionRowInfo>
                                      <OptionRowName $active={qty > 0}>{s.name}</OptionRowName>
                                    </OptionRowInfo>
                                    {s.price > 0 && <OptionRowPrice>+R$ {(s.price * Math.max(qty, 1)).toFixed(2)}</OptionRowPrice>}
                                    <div style={{display:'flex', alignItems:'center', gap:6, marginLeft:8}} onClick={e => e.stopPropagation()}>
                                      <button onClick={() => updateSauceQty(item.id, s.name, -1, item.product)} style={{width:28, height:28, borderRadius:'50%', border:'1px solid #e2e8f0', background: qty > 0 ? '#fee2e2' : '#f1f5f9', color: qty > 0 ? '#ef4444' : '#94a3b8', cursor:'pointer', fontWeight:700, fontSize:16, display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1}}>−</button>
                                      <span style={{minWidth:18, textAlign:'center', fontWeight:700, fontSize:'0.9rem'}}>{qty}</span>
                                      <button onClick={() => updateSauceQty(item.id, s.name, +1, item.product)} style={{width:28, height:28, borderRadius:'50%', border:'1px solid #e2e8f0', background:'#ea580c', color:'white', cursor:'pointer', fontWeight:700, fontSize:16, display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1}}>+</button>
                                    </div>
                                  </OptionRow>
                                );
                              }
                              const isActive = !!item.options?.sauces?.includes(s.name);
                              return (
                                <OptionRow key={s.id || s._id} $active={isActive} onClick={() => toggleSauce(item.id, s.name, item.product)}>
                                  <OptionCheckbox $active={isActive}>
                                    {isActive && <span style={{color:'white', fontSize:'10px', fontWeight:700, lineHeight:1}}>✓</span>}
                                  </OptionCheckbox>
                                  <OptionRowInfo>
                                    <OptionRowName $active={isActive}>{s.name}</OptionRowName>
                                  </OptionRowInfo>
                                  {s.price > 0 && <OptionRowPrice>+R$ {s.price.toFixed(2)}</OptionRowPrice>}
                                </OptionRow>
                              );
                            })}
                          </OptionTable>
                        </CollapsibleItemOptions>
                      );
                    })()}

                    {!item.product.isCombo && item.product.hasFlavor && (
                      <CollapsibleItemOptions label={<>{item.product.flavorLimit ? `Escolha ${item.product.flavorLimit.toString().padStart(2, '0')} Sabor(es)` : 'Sabores'} <span style={{color: '#ef4444'}}>* Obrigatório</span></>} closeWhen={(item.options?.flavors?.length || 0) >= (item.product.flavorLimit || 1)}>
                        {(() => {
                          const productFlavors = getProductFlavors(item.product);
                          const hasClassifications = productFlavors.some((f: any) => f.classificationId);
                          if (!hasClassifications) {
                            return (
                              <OptionTable>
                                {productFlavors.map((f: any) => {
                                  const isActive = !!item.options?.flavors?.includes(f.name);
                                  return (
                                    <OptionRow key={f.id || f._id} $active={isActive} onClick={() => toggleFlavor(item.id, f.name, item.product)}>
                                      <OptionCheckbox $active={isActive}>{isActive && <span style={{color:'white', fontSize:'10px', fontWeight:700, lineHeight:1}}>✓</span>}</OptionCheckbox>
                                      <OptionRowInfo>
                                        <OptionRowName $active={isActive}>{f.name}</OptionRowName>
                                        {f.description && <OptionRowDesc>{f.description}</OptionRowDesc>}
                                      </OptionRowInfo>
                                      {f.price > 0 && <OptionRowPrice>+R$ {f.price.toFixed(2)}</OptionRowPrice>}
                                    </OptionRow>
                                  );
                                })}
                              </OptionTable>
                            );
                          }
                          const classMap: Record<string, any> = {};
                          (flavorClassifications || []).forEach((c: any) => { classMap[c.id || c._id] = c; });
                          const groups: Record<string, { classification: any; flavors: any[] }> = {};
                          const ungrouped: any[] = [];
                          productFlavors.forEach((f: any) => {
                            const cid = f.classificationId?._id || f.classificationId;
                            if (cid && classMap[cid]) {
                              if (!groups[cid]) groups[cid] = { classification: classMap[cid], flavors: [] };
                              groups[cid].flavors.push(f);
                            } else {
                              ungrouped.push(f);
                            }
                          });
                          const sortedGroups = Object.values(groups).sort((a: any, b: any) => (a.classification.order || 0) - (b.classification.order || 0));
                          if (ungrouped.length) sortedGroups.push({ classification: null, flavors: ungrouped });
                          return (
                            <>
                              {sortedGroups.map((group: any, gi: number) => (
                                <div key={group.classification?.id || gi}>
                                  {group.classification && (
                                    <div style={{ padding: '0.5rem 0.75rem 0.25rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                      {group.classification.name}
                                    </div>
                                  )}
                                  <OptionTable>
                                    {group.flavors.map((f: any) => {
                                      const isActive = !!item.options?.flavors?.includes(f.name);
                                      return (
                                        <OptionRow key={f.id || f._id} $active={isActive} onClick={() => toggleFlavor(item.id, f.name, item.product)}>
                                          <OptionCheckbox $active={isActive}>{isActive && <span style={{color:'white', fontSize:'10px', fontWeight:700, lineHeight:1}}>✓</span>}</OptionCheckbox>
                                          <OptionRowInfo>
                                            <OptionRowName $active={isActive}>{f.name}</OptionRowName>
                                            {f.description && <OptionRowDesc>{f.description}</OptionRowDesc>}
                                          </OptionRowInfo>
                                          {f.price > 0 && <OptionRowPrice>+R$ {f.price.toFixed(2)}</OptionRowPrice>}
                                        </OptionRow>
                                      );
                                    })}
                                  </OptionTable>
                                </div>
                              ))}
                            </>
                          );
                        })()}
                      </CollapsibleItemOptions>
                    )}

                    {!item.product.isCombo && item.product.hasSideDish && (
                      <CollapsibleItemOptions closeWhen={(item.options?.sideDishes?.length || 0) >= (item.product.sideDishLimit || 1)} label={<>
                          {(item.product as any).sideDishAsPrepMode
                            ? (item.product.sideDishLimit ? `Escolha ${item.product.sideDishLimit.toString().padStart(2, '0')} Modo(s) de Preparo` : 'Modo de Preparo')
                            : (item.product.sideDishLimit ? `Escolha ${item.product.sideDishLimit.toString().padStart(2, '0')} Acompanhamento(s)` : 'Acompanhamento')
                          }
                          {' '}<span style={{color: '#ef4444'}}>* Obrigatório</span>
                        </>}>
                        <OptionTable>
                          {getProductSideDishes(item.product).map((s: any) => {
                            const isActive = !!item.options?.sideDishes?.includes(s.name);
                            return (
                              <OptionRow key={s.id || s._id} $active={isActive} onClick={() => toggleSideDish(item.id, s.name, item.product)}>
                                <OptionCheckbox $active={isActive}>
                                  {isActive && <span style={{color:'white', fontSize:'10px', fontWeight:700, lineHeight:1}}>✓</span>}
                                </OptionCheckbox>
                                <OptionRowInfo>
                                  <OptionRowName $active={isActive}>{s.name}</OptionRowName>
                                </OptionRowInfo>
                                {s.price > 0 && <OptionRowPrice>+R$ {s.price.toFixed(2)}</OptionRowPrice>}
                              </OptionRow>
                            );
                          })}
                        </OptionTable>
                      </CollapsibleItemOptions>
                    )}

                    {!item.product.isCombo && (item.product as any).hasIncludedAdditionals && (
                      <CollapsibleItemOptions label={<>Adicionais Inclusos <span style={{color:'#059669', fontSize:'0.75rem', fontWeight:700}}>* Obrigatório · Grátis</span></>} closeWhen={Object.values((item.options?.includedAdditionalQtys || {}) as Record<string,number>).reduce((s, q) => s + q, 0) >= ((item.product as any).includedAdditionalLimit || 1)}>
                        <div style={{fontSize:'0.75rem', color:'#059669', padding:'4px 12px 2px', marginBottom:'4px'}}>
                          Escolha até {(item.product as any).includedAdditionalLimit || 1} adicional(ais) incluso(s) sem custo.
                        </div>
                        <OptionTable>
                          {getProductIncludedAdditionals(item.product).map((a: any) => {
                            const qty = ((item.options?.includedAdditionalQtys || {}) as Record<string,number>)[a.name] || 0;
                            return (
                              <div key={a.id || a._id} style={{display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderBottom:'1px dashed #e2e8f0', background: qty > 0 ? 'rgba(5,150,105,0.06)' : 'white'}}>
                                <OptionRowInfo>
                                  <OptionRowName $active={qty > 0}>{a.name}</OptionRowName>
                                  <OptionRowDesc style={{color:'#059669', fontWeight:600}}>Grátis</OptionRowDesc>
                                </OptionRowInfo>
                                {qty === 0 ? (
                                  <button onClick={() => changeIncludedAdditionalQty(item.id, a.name, 1, item.product)} style={{padding:'4px 10px', borderRadius:'6px', background:'#f0fdf4', border:'1px solid #bbf7d0', cursor:'pointer', fontSize:'0.75rem', fontWeight:600, color:'#059669', flexShrink:0}}>+ Incluir</button>
                                ) : (
                                  <div style={{display:'flex', alignItems:'center', gap:'6px', flexShrink:0}}>
                                    <button onClick={() => changeIncludedAdditionalQty(item.id, a.name, -1, item.product)} style={{width:'24px', height:'24px', borderRadius:'50%', background:'#6b7280', color:'white', border:'none', cursor:'pointer', fontWeight:700}}>−</button>
                                    <span style={{fontWeight:700, minWidth:'16px', textAlign:'center'}}>{qty}</span>
                                    <button onClick={() => changeIncludedAdditionalQty(item.id, a.name, 1, item.product)} style={{width:'24px', height:'24px', borderRadius:'50%', background:'#059669', color:'white', border:'none', cursor:'pointer', fontWeight:700}}>+</button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </OptionTable>
                      </CollapsibleItemOptions>
                    )}

                    {!item.product.isCombo && item.product.hasAdditional && (
                      (item.product as any).adicionalSimples ? (
                        <CollapsibleItemOptions label={<>
                          Adicionais
                          {' '}<span style={{color:'#6b7280', fontSize:'0.75rem'}}>(Opcional{!getExtrasChargeAdditionals(item.product) ? ' · Grátis' : ''})</span>
                        </>}>
                          <OptionTable>
                            {getProductAdditionals(item.product).map((a: any) => {
                              const isActive = !!((item.options?.additionals || []) as string[]).includes(a.name);
                              const charge = getExtrasChargeAdditionals(item.product);
                              return (
                                <OptionRow key={a.id || a._id} $active={isActive} onClick={() => toggleSimpleAdditional(item.id, a.name)}>
                                  <OptionCheckbox $active={isActive}>
                                    {isActive && <span style={{color:'white', fontSize:'10px', fontWeight:700, lineHeight:1}}>✓</span>}
                                  </OptionCheckbox>
                                  <OptionRowInfo>
                                    <OptionRowName $active={isActive}>{a.name}</OptionRowName>
                                    {charge
                                      ? a.price > 0 && <OptionRowDesc>+R$ {a.price.toFixed(2)}</OptionRowDesc>
                                      : <OptionRowDesc style={{color:'#059669', fontWeight:600}}>Grátis</OptionRowDesc>
                                    }
                                  </OptionRowInfo>
                                </OptionRow>
                              );
                            })}
                          </OptionTable>
                        </CollapsibleItemOptions>
                      ) : (
                        <CollapsibleItemOptions label={<>
                          {item.product.additionalLimit ? `Adicionais Extras (até ${item.product.additionalLimit})` : 'Adicionais Extras'}
                          {' '}<span style={{color:'#6b7280', fontSize:'0.75rem'}}>(Opcional{!getExtrasChargeAdditionals(item.product) ? ' · Grátis' : ''})</span>
                        </>}>
                          <OptionTable>
                            {getProductAdditionals(item.product).map((a: any) => {
                              const qty = ((item.options?.additionalQtys || {}) as Record<string,number>)[a.name] || 0;
                              const charge = getExtrasChargeAdditionals(item.product);
                              return (
                                <div key={a.id || a._id} style={{display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderBottom:'1px dashed #e2e8f0', background: qty > 0 ? 'rgba(245,130,32,0.06)' : 'white'}}>
                                  <OptionRowInfo>
                                    <OptionRowName $active={qty > 0}>{a.name}</OptionRowName>
                                    {charge
                                      ? a.price > 0 && <OptionRowDesc>+R$ {a.price.toFixed(2)} cada</OptionRowDesc>
                                      : <OptionRowDesc style={{color:'#059669', fontWeight:600}}>Grátis</OptionRowDesc>
                                    }
                                  </OptionRowInfo>
                                  {qty === 0 ? (
                                    <button onClick={() => changeAdditionalQty(item.id, a.name, 1, item.product)} style={{padding:'4px 10px', borderRadius:'6px', background:'#f1f5f9', border:'1px solid #e2e8f0', cursor:'pointer', fontSize:'0.75rem', fontWeight:600, color:'#374151', flexShrink:0}}>+ Adicionar</button>
                                  ) : (
                                    <div style={{display:'flex', alignItems:'center', gap:'6px', flexShrink:0}}>
                                      <button onClick={() => changeAdditionalQty(item.id, a.name, -1, item.product)} style={{width:'24px', height:'24px', borderRadius:'50%', background:'#ea580c', color:'white', border:'none', cursor:'pointer', fontWeight:700}}>−</button>
                                      <span style={{fontWeight:700, minWidth:'16px', textAlign:'center'}}>{qty}</span>
                                      <button onClick={() => changeAdditionalQty(item.id, a.name, 1, item.product)} style={{width:'24px', height:'24px', borderRadius:'50%', background:'#10b981', color:'white', border:'none', cursor:'pointer', fontWeight:700}}>+</button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </OptionTable>
                        </CollapsibleItemOptions>
                      )
                    )}

                    {!item.product.isCombo && item.product.hasSalad && (
                      <CollapsibleItemOptions label={<>{item.product.saladLimit ? `Escolha ${item.product.saladLimit.toString().padStart(2, '0')} Salada(s)` : 'Saladas'} <span style={{color: '#ef4444'}}>* Obrigatório</span></>}>
                        <OptionTable>
                          {getProductSalads(item.product).map((s: any) => {
                            const isActive = !!item.options?.salads?.includes(s.name);
                            return (
                              <OptionRow key={s.id || s._id} $active={isActive} onClick={() => toggleSalad(item.id, s.name, item.product)}>
                                <OptionCheckbox $active={isActive}>
                                  {isActive && <span style={{color:'white', fontSize:'10px', fontWeight:700, lineHeight:1}}>✓</span>}
                                </OptionCheckbox>
                                <OptionRowInfo>
                                  <OptionRowName $active={isActive}>{s.name}</OptionRowName>
                                </OptionRowInfo>
                              </OptionRow>
                            );
                          })}
                        </OptionTable>
                      </CollapsibleItemOptions>
                    )}

                    {!item.product.isCombo && item.product.hasDessert && (
                      <CollapsibleItemOptions label={<>{item.product.dessertLimit ? `Escolha ${item.product.dessertLimit.toString().padStart(2, '0')} Sobremesa(s)` : 'Sobremesas'} <span style={{color: '#ef4444'}}>* Obrigatório</span></>}>
                        <OptionTable>
                          {getProductDesserts(item.product).map((d: any) => {
                            const isActive = !!item.options?.desserts?.includes(d.name);
                            return (
                              <OptionRow key={d.id || d._id} $active={isActive} onClick={() => toggleDessert(item.id, d.name, item.product)}>
                                <OptionCheckbox $active={isActive}>
                                  {isActive && <span style={{color:'white', fontSize:'10px', fontWeight:700, lineHeight:1}}>✓</span>}
                                </OptionCheckbox>
                                <OptionRowInfo>
                                  <OptionRowName $active={isActive}>{d.name}</OptionRowName>
                                </OptionRowInfo>
                              </OptionRow>
                            );
                          })}
                        </OptionTable>
                      </CollapsibleItemOptions>
                    )}

                    {!item.product.isCombo && item.product.hasIngredient && (
                      <CollapsibleItemOptions label={<>Retirar Ingredientes (Opcional)</>}>
                        <OptionTable>
                          {getProductIngredients(item.product).map((i: any) => {
                            const isRemoved = !!item.options?.removedIngredients?.includes(i.name);
                            return (
                              <OptionRow
                                key={i.id || i._id}
                                $active={isRemoved}
                                onClick={() => isRemoved ? toggleIngredient(item.id, i.name) : setIngredientConfirm({ itemId: item.id, ingredient: i.name })}
                              >
                                <OptionCheckbox $active={isRemoved}>
                                  {isRemoved && <span style={{color:'white', fontSize:'10px', fontWeight:700, lineHeight:1}}>✓</span>}
                                </OptionCheckbox>
                                <OptionRowInfo>
                                  <OptionRowName $active={isRemoved}>{isRemoved ? `Sem ${i.name}` : i.name}</OptionRowName>
                                </OptionRowInfo>
                              </OptionRow>
                            );
                          })}
                        </OptionTable>
                      </CollapsibleItemOptions>
                    )}

                    <ItemOptions>
                      <OptionLabel>
                        Observações <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>(Opcional)</span>
                      </OptionLabel>
                      <Textarea
                        placeholder="Ex: sem cebola, bem passado..."
                        value={item.options?.observations || ''}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                          updateQuantity(item.id, item.quantity, { ...item.options, observations: e.target.value })
                        }
                        maxLength={150}
                        style={{ marginTop: '4px', minHeight: '60px', resize: 'vertical' }}
                      />
                    </ItemOptions>
                  </CartItem>
                ))}
              </CartItems>

              {/* Banner de modo comanda QR — oculta o seletor de recebimento */}
              {(deliveryType === 'comanda-simples' || deliveryType === 'comanda-cartao') ? (
                <PickupAddressSection style={{ marginBottom: '15px', background: '#fff7ed', borderColor: '#fed7aa' }}>
                  <PickupAddressTitle style={{ color: '#ea580c' }}>
                    <i className="fa-solid fa-utensils"></i>{' '}
                    {deliveryType === 'comanda-simples' ? `Mesa ${comandaMesaNumber}` : `Cartão #${cartaoCode}`}
                  </PickupAddressTitle>
                  <PickupAddressText>
                    {deliveryType === 'comanda-simples'
                      ? 'Seu pedido será registrado na comanda da mesa.'
                      : 'Seu pedido será registrado na comanda do cartão.'}
                  </PickupAddressText>
                </PickupAddressSection>
              ) : (
                <DeliverySection>
                  <SectionTitle>Recebimento</SectionTitle>
                  <DeliveryOptions>
                    <DeliveryOption $active={deliveryType === 'delivery'} onClick={() => setDeliveryType('delivery')}><DeliveryInfo><DeliveryLabel>Delivery</DeliveryLabel><DeliveryFee>R$ {deliveryFee.toFixed(2)}</DeliveryFee></DeliveryInfo></DeliveryOption>
                    <DeliveryOption $active={deliveryType === 'pickup'} onClick={() => setDeliveryType('pickup')}><DeliveryInfo><DeliveryLabel>Retirada</DeliveryLabel><DeliveryFee $free>Grátis</DeliveryFee></DeliveryInfo></DeliveryOption>
                    {store?.comandaEnabled && !store?.clienteNaoFazPedidos && <DeliveryOption $active={deliveryType === 'table'} onClick={() => setDeliveryType('table')}><DeliveryInfo><DeliveryLabel>Na Mesa</DeliveryLabel><DeliveryFee $free>Comanda</DeliveryFee></DeliveryInfo></DeliveryOption>}
                  </DeliveryOptions>
                </DeliverySection>
              )}

              {deliveryType === 'table' && <PickupAddressSection style={{ marginBottom: '15px' }}><PickupAddressTitle><i className="fa-solid fa-utensils"></i> Mesa</PickupAddressTitle><PickupAddressText>Informe a mesa e senha.</PickupAddressText></PickupAddressSection>}

              {(deliveryType === 'comanda-simples' || deliveryType === 'comanda-cartao') && hasActiveComandaSession ? null : (
              <FormSection>
                <SectionTitle>Seus Dados</SectionTitle>
                {(deliveryType === 'comanda-simples' || deliveryType === 'comanda-cartao') ? (
                  <>
                    <Input type="text" placeholder="Seu nome" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} />
                    <Input type="tel" placeholder="Telefone" value={formData.phone} onChange={e => handlePhoneChange(e.target.value)} maxLength={15} />
                  </>
                ) : (
                  <>
                    <Input type="text" placeholder="Nome completo" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} />
                    <Input type="tel" placeholder="Telefone" value={formData.phone} onChange={e => handlePhoneChange(e.target.value)} maxLength={15} />
                    {deliveryType === 'delivery' && (() => {
                      const storeAny = store as any;
                      const isVariable = storeAny?.deliveryFeeType === 'variable';
                      const neighborhoods: { nome: string; valor: number }[] = storeAny?.deliveryNeighborhoods || [];
                      const aceitaInterior = storeAny?.aceitaInterior || false;
                      const valorPorKm = storeAny?.interiorValorPorKm || 0;
                      return (
                        <>
                          {isVariable && neighborhoods.length > 0 ? (
                            <select
                              value={formData.bairro}
                              onChange={e => setFormData(p => ({ ...p, bairro: e.target.value, isInterior: false }))}
                              style={{ width: '100%', padding: '12px 14px', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '0.95rem', background: 'white', color: formData.bairro ? '#1e293b' : '#94a3b8', appearance: 'none' }}
                            >
                              <option value="">Selecione o bairro *</option>
                              {neighborhoods.map(nb => (
                                <option key={nb.nome} value={nb.nome}>{nb.nome} — R$ {nb.valor.toFixed(2)}</option>
                              ))}
                              {aceitaInterior && <option value="__interior__">Interior / Zona Rural</option>}
                            </select>
                          ) : (
                            <Input type="text" placeholder="Bairro *" value={formData.bairro} onChange={e => setFormData(p => ({ ...p, bairro: e.target.value }))} />
                          )}
                          {formData.bairro === '__interior__' && aceitaInterior && (
                            <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: '10px', padding: '10px 14px', fontSize: '0.82rem', color: '#854d0e' }}>
                              <div style={{ fontWeight: 700, marginBottom: 6 }}>Entrega no Interior</div>
                              <Input type="number" placeholder="Distância em km" value={formData.kmInterior || ''} onChange={e => setFormData(p => ({ ...p, kmInterior: e.target.value }))} style={{ marginBottom: 6 }} />
                              {formData.kmInterior && parseFloat(formData.kmInterior) > 0 && valorPorKm > 0 && (
                                <div style={{ fontWeight: 700, color: '#92400e' }}>Estimativa: R$ {(parseFloat(formData.kmInterior) * valorPorKm).toFixed(2)}</div>
                              )}
                              <div style={{ marginTop: 6, fontSize: '0.78rem', color: '#92400e' }}>O preço do frete pode variar. O estabelecimento entrará em contato para confirmar.</div>
                            </div>
                          )}
                          <Textarea placeholder="Logradouro (Ex.: Rua, Avenida...) *" value={formData.address} onChange={e => setFormData(p => ({ ...p, address: e.target.value }))} />
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px' }}>
                            <Input type="text" placeholder="Número *" value={formData.numero} onChange={e => setFormData(p => ({ ...p, numero: e.target.value }))} />
                            <Input type="text" placeholder="Complemento (opcional)" value={formData.complemento} onChange={e => setFormData(p => ({ ...p, complemento: e.target.value }))} />
                          </div>
                          <Input type="text" placeholder="Referência (opcional)" value={formData.reference} onChange={e => setFormData(p => ({ ...p, reference: e.target.value }))} />
                        </>
                      );
                    })()}
                    {deliveryType === 'table' && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}><Input type="number" placeholder="Mesa" value={formData.mesa} onChange={e => setFormData(p => ({ ...p, mesa: e.target.value }))} /><Input type="password" placeholder="Senha" value={formData.password} onChange={e => setFormData(p => ({ ...p, password: e.target.value }))} /></div>}
                  </>
                )}
              </FormSection>
              )}

              {((store as any)?.hasActiveCoupons || appliedCupom) && (
                <PaymentSection>
                  <SectionTitle>Cupom</SectionTitle>
                  {appliedCupom ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', background: '#ecfdf5', padding: '12px', borderRadius: '12px', border: '1px solid #10b981' }}><div><div style={{ fontSize: '0.7rem', color: '#047857' }}>CUPOM: {appliedCupom}</div></div><button onClick={removeCupom} style={{ background: 'none', border: 'none', color: '#ef4444', fontWeight: 'bold', cursor: 'pointer' }}>REMOVER</button></div>
                  ) : (
                    <div style={{ display: 'flex', gap: '10px' }}><Input style={{ flex: 1, margin: 0 }} placeholder="Código" value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())} /><button onClick={handleApplyCoupon} disabled={isApplyingCoupon || !couponCode.trim()} style={{ background: '#ea580c', color: 'white', border: 'none', padding: '0 15px', borderRadius: '12px', fontWeight: 'bold' }}>{isApplyingCoupon ? '...' : 'OK'}</button></div>
                  )}
                </PaymentSection>
              )}

              {(deliveryType !== 'comanda-simples' && deliveryType !== 'comanda-cartao') && (() => {
                const isFiscalMode = !isAutomated && !!(store as any)?.paymentMethodsReady
                  && visibleFiscalMethods.length > 0 && deliveryType !== 'table';

                if (isAutomated && deliveryType !== 'table') {
                  return (
                    <PaymentSection>
                      <SectionTitle>Forma de Pagamento</SectionTitle>
                      <PaymentOptions>
                        <PaymentOption>
                          <input type="radio" checked={formData.paymentMethod === 'pix'} onChange={() => setFormData(p => ({ ...p, paymentMethod: 'pix' }))} />
                          PIX
                        </PaymentOption>
                        <PaymentOption>
                          <input type="radio" checked={formData.paymentMethod === 'card'} onChange={() => setFormData(p => ({ ...p, paymentMethod: 'card' }))} />
                          Cartão {maxInstallments > 1 ? `(até ${maxInstallments}×)` : '(à vista)'}
                        </PaymentOption>
                        {allowCashPayment && (
                          <PaymentOption>
                            <input type="radio" checked={formData.paymentMethod === 'cash'} onChange={() => setFormData(p => ({ ...p, paymentMethod: 'cash' }))} />
                            Dinheiro
                          </PaymentOption>
                        )}
                      </PaymentOptions>
                      {isCardSelected && cardBrands.length > 0 && (
                        <button onClick={() => setShowBandeirasModal(true)} style={{background:'none', border:'1px solid #bfdbfe', borderRadius:'8px', padding:'6px 14px', marginTop:'8px', fontSize:'0.8rem', color:'#2563eb', fontWeight:600, cursor:'pointer'}}>
                          Ver bandeiras aceitas
                        </button>
                      )}
                      {isCashSelected && (
                        <Input type="number" placeholder="Troco para? (opcional)" value={formData.changeFor} onChange={e => setFormData(p => ({ ...p, changeFor: e.target.value }))} />
                      )}
                      {isClassicCard && isCardSelected && (
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '10px 14px', marginTop: '10px', fontSize: '0.82rem', color: '#1d4ed8', lineHeight: 1.5 }}>
                          Pagamento por cartão: o estabelecimento levará a maquininha até você.
                        </div>
                      )}
                    </PaymentSection>
                  );
                }

                if (isFiscalMode && visibleFiscalMethods.length > 0) {
                  const selVariacoes: any[] = selectedFiscalPM?.variacoes || [];
                  return (
                    <PaymentSection>
                      <SectionTitle>Forma de Pagamento</SectionTitle>
                      <PaymentOptions>
                        {visibleFiscalMethods.map((pm: any, idx: number) => (
                          <PaymentOption key={idx}>
                            <input
                              type="radio"
                              checked={selectedFiscalPM?.refFiscal === pm.refFiscal}
                              onChange={() => { setSelectedFiscalPM(pm); setSelectedVariacaoIdx(-1); }}
                            />
                            {pm.nome}
                          </PaymentOption>
                        ))}
                      </PaymentOptions>

                      {/* Seleção de bandeira/variação (modo clássico) */}
                      {selVariacoes.length > 0 && (
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#374151', marginBottom: '8px' }}>
                            Bandeira <span style={{ color: '#ef4444' }}>* obrigatório</span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {selVariacoes.map((v: any, i: number) => {
                              const nome = variacaoNome(v);
                              const hasTaxa = !!v.taxaAtiva && (parseFloat(v.taxaValor) || 0) > 0 && v.taxaPagador === 'cliente';
                              return (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => setSelectedVariacaoIdx(i)}
                                  style={{
                                    padding: '8px 14px',
                                    borderRadius: '10px',
                                    border: `2px solid ${selectedVariacaoIdx === i ? 'var(--primary, #f58220)' : '#e2e8f0'}`,
                                    background: selectedVariacaoIdx === i ? '#fff7ed' : 'white',
                                    color: selectedVariacaoIdx === i ? '#ea580c' : '#374151',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                  }}
                                >
                                  {nome}
                                  {hasTaxa && (
                                    <span style={{ fontSize: '0.72rem', color: '#b45309' }}>
                                      +{v.taxaTipo === 'fixo' ? `R$${v.taxaValor}` : `${v.taxaValor}%`}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '6px' }}>
                            O estabelecimento levará a maquininha correspondente.
                          </div>
                        </div>
                      )}

                      {/* Taxa do cliente */}
                      {paymentFee > 0 && (
                        <div style={{ marginTop: '10px', background: '#fef9c3', border: '1px solid #fde047', borderRadius: '8px', padding: '8px 12px', fontSize: '0.82rem', color: '#854d0e' }}>
                          Taxa do cartão incluída: +R$ {paymentFee.toFixed(2)}
                        </div>
                      )}

                      {/* Troco — apenas quando o PM fiscal tem precisaTroco */}
                      {selectedFiscalPM?.precisaTroco && (
                        <div style={{ marginTop: '12px' }}>
                          <Input
                            type="number"
                            placeholder="Troco para? (opcional)"
                            value={formData.changeFor}
                            onChange={e => setFormData(p => ({ ...p, changeFor: e.target.value }))}
                          />
                        </div>
                      )}
                    </PaymentSection>
                  );
                }

                return (
                  <PaymentSection>
                    <SectionTitle>Pagamento</SectionTitle>
                    <PaymentOptions>
                      <PaymentOption><input type="radio" checked={formData.paymentMethod === 'pix'} onChange={() => setFormData(p => ({ ...p, paymentMethod: 'pix' }))} /> PIX</PaymentOption>
                      <PaymentOption><input type="radio" checked={formData.paymentMethod === 'card'} onChange={() => setFormData(p => ({ ...p, paymentMethod: 'card' }))} /> Cartão</PaymentOption>
                      <PaymentOption><input type="radio" checked={formData.paymentMethod === 'cash'} onChange={() => setFormData(p => ({ ...p, paymentMethod: 'cash' }))} /> Dinheiro</PaymentOption>
                    </PaymentOptions>
                    {isCardSelected && cardBrands.length > 0 && (
                      <button onClick={() => setShowBandeirasModal(true)} style={{background:'none', border:'1px solid #bfdbfe', borderRadius:'8px', padding:'6px 14px', marginTop:'8px', fontSize:'0.8rem', color:'#2563eb', fontWeight:600, cursor:'pointer'}}>
                        Ver bandeiras aceitas
                      </button>
                    )}
                    {formData.paymentMethod === 'cash' && <Input type="number" placeholder="Troco para?" value={formData.changeFor} onChange={e => setFormData(p => ({ ...p, changeFor: e.target.value }))} />}
                  </PaymentSection>
                );
              })()}

              <TotalSection>
                <TotalRow><TotalLabel>Subtotal</TotalLabel><TotalValue>R$ {(subtotal - extraCosts).toFixed(2)}</TotalValue></TotalRow>
                {extraCosts > 0 && <TotalRow><TotalLabel>Adicionais</TotalLabel><TotalValue>R$ {extraCosts.toFixed(2)}</TotalValue></TotalRow>}
                {promotionDiscount > 0 && <TotalRow><TotalLabel style={{color:'#f97316'}}>Promoção</TotalLabel><TotalValue style={{color:'#f97316'}}>- R$ {promotionDiscount.toFixed(2)}</TotalValue></TotalRow>}
                {discountAmount > 0 && <TotalRow><TotalLabel style={{color:'#10b981'}}>Cupom</TotalLabel><TotalValue style={{color:'#10b981'}}>- R$ {discountAmount.toFixed(2)}</TotalValue></TotalRow>}
                <TotalRow><TotalLabel>Entrega</TotalLabel><TotalValue>{deliveryFee > 0 ? `R$ ${deliveryFee.toFixed(2)}` : 'Grátis'}</TotalValue></TotalRow>
                {paymentFee > 0 && <TotalRow><TotalLabel style={{color:'#b45309'}}>Taxa pagamento</TotalLabel><TotalValue style={{color:'#b45309'}}>+ R$ {paymentFee.toFixed(2)}</TotalValue></TotalRow>}
                <TotalRow $highlight><TotalLabel $highlight>Total</TotalLabel><TotalValue $highlight>R$ {localTotal.toFixed(2)}</TotalValue></TotalRow>
              </TotalSection>

              <KeepShoppingButton onClick={onClose}>
                <i className="fas fa-arrow-left"></i> CONTINUAR COMPRANDO
              </KeepShoppingButton>
              
              <CheckoutButton onClick={handleCheckout} disabled={isLoadingPayment}>
                {(deliveryType === 'comanda-simples' || deliveryType === 'comanda-cartao') ? (
                  isLoadingPayment ? 'REGISTRANDO...' : <><i className="fa-solid fa-utensils" style={{ marginRight: '8px' }}></i>ADICIONAR À COMANDA</>
                ) : shouldUseAutomatedFlow ? (
                  isLoadingPayment ? 'CARREGANDO...' : 'IR PARA O PAGAMENTO'
                ) : isClassicPix ? (
                  isLoadingPayment ? 'REGISTRANDO...' : 'PROSSEGUIR'
                ) : isGestorMode ? (
                  isLoadingPayment ? 'REGISTRANDO...' : 'FINALIZAR PEDIDO'
                ) : (
                  <><WhatsAppIcon><i className="fab fa-whatsapp"></i></WhatsAppIcon>{deliveryType === 'table' ? 'ADICIONAR À COMANDA' : 'PEDIR NO WHATSAPP'}</>
                )}
              </CheckoutButton>
            </>
          )}
        </>
      )}

      {paymentStep === 'payment' && store?.mercadoPagoConfig?.publicKey && (
        <div style={{ padding: '20px 0' }}>
            <SectionTitle>Pagamento com Cartão</SectionTitle>
            <PaymentBrick
                publicKey={store.mercadoPagoConfig.publicKey}
                amount={localTotal}
                maxInstallments={maxInstallments}
                onReady={() => {}}
                onError={(_err) => {
                    showToast('Erro ao carregar o pagamento. Tente novamente.', 'error');
                    setPaymentStep('info');
                }}
                onSubmit={handlePaymentSubmit}
            />
            {isLoadingPayment && (
                <div style={{ textAlign: 'center', padding: '12px', color: '#6b7280', fontWeight: 700 }}>
                    Processando pagamento...
                </div>
            )}
            <button
                onClick={() => setPaymentStep('info')}
                style={{ width: '100%', padding: '15px', marginTop: '10px', background: 'none', border: 'none', color: '#666', fontWeight: 'bold', cursor: 'pointer' }}
            >
                VOLTAR PARA O CARRINHO
            </button>
        </div>
      )}

      {paymentStep === 'pix' && pixData && (
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <SectionTitle>Pague com PIX</SectionTitle>

            {pixData.qr_code_base64 && (
                <img
                    src={`data:image/png;base64,${pixData.qr_code_base64}`}
                    alt="QR Code PIX"
                    style={{ width: 200, height: 200, margin: '0 auto 20px', display: 'block', borderRadius: 12 }}
                />
            )}

            <div style={{ marginBottom: '16px', textAlign: 'left' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Código PIX Copia e Cola
                </div>
                <PixContainer>
                    <PixKeyWrapper>
                        <PixKey>{pixData.qr_code}</PixKey>
                        <CopyButton onClick={() => handleCopyPix(pixData.qr_code)}>COPIAR</CopyButton>
                    </PixKeyWrapper>
                </PixContainer>
            </div>

            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '14px 16px', marginBottom: 16, textAlign: 'left' }}>
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#92400e', lineHeight: 1.55 }}>
                    Caro cliente, se optar pela chave copia cola, por gentileza copie a chave e após o
                    pagamento retorne ao app e clique no botão <strong>Finalizar Pedido</strong>. Se não
                    puder retornar ao app, a loja enviará uma confirmação de pedido no seu WhatsApp
                    dentro de alguns minutos.
                </p>
                {requirePaymentBeforeOrder && (
                    <p style={{ margin: '10px 0 0', fontSize: '0.82rem', color: '#92400e', lineHeight: 1.55 }}>
                        <strong>Essa loja optou por receber pedidos apenas após o pagamento confirmado.</strong>{' '}
                        Agradecemos a compreensão.
                    </p>
                )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '20px', color: '#10b981' }}>
                <i className="fas fa-spinner fa-spin"></i>
                <span style={{ fontSize: '0.875rem', fontWeight: 700 }}>Aguardando confirmação do pagamento...</span>
            </div>

            <button
                onClick={() => setPaymentStep('info')}
                style={{ width: '100%', padding: '15px', background: 'none', border: 'none', color: '#666', fontWeight: 'bold', cursor: 'pointer' }}
            >
                VOLTAR
            </button>
        </div>
      )}

      {paymentStep === 'classic-pix' && (
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <i className="fas fa-check" style={{ fontSize: '1.75rem', color: '#16a34a' }}></i>
            </div>
            <h2 style={{ fontWeight: 900, color: '#111827', marginBottom: '4px', fontSize: '1.25rem' }}>Pedido Registrado!</h2>
            <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '0.9rem', lineHeight: 1.5 }}>
                Copie a chave PIX abaixo e realize o pagamento para confirmar seu pedido.
            </p>

            <div style={{ marginBottom: '16px', textAlign: 'left' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Chave PIX
                </div>
                <PixContainer>
                    <PixKeyWrapper>
                        <PixKey>{(store as any)?.pixKey}</PixKey>
                        <CopyButton onClick={() => handleCopyPix((store as any)?.pixKey)}>COPIAR</CopyButton>
                    </PixKeyWrapper>
                </PixContainer>
            </div>

            {isGestorMode ? (
                <button
                    onClick={handleClassicPixWhatsApp}
                    style={{ width: '100%', padding: '16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '16px', fontWeight: 900, fontSize: '1rem', cursor: 'pointer' }}
                >
                    FINALIZAR
                </button>
            ) : (
                <>
                <button
                    onClick={handleClassicPixWhatsApp}
                    style={{ width: '100%', padding: '16px', background: '#25d366', color: 'white', border: 'none', borderRadius: '16px', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '12px' }}
                >
                    <i className="fab fa-whatsapp" style={{ fontSize: '1.3rem' }}></i>
                    RECEBER MEU RESUMO NO WHATSAPP
                </button>
                <button
                    onClick={() => setPaymentStep('info')}
                    style={{ width: '100%', padding: '15px', background: 'none', border: 'none', color: '#666', fontWeight: 'bold', cursor: 'pointer' }}
                >
                    VOLTAR
                </button>
                </>
            )}
        </div>
      )}

      {paymentStep === 'payment_confirmed' && (
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <i className="fas fa-check" style={{ fontSize: '2rem', color: '#16a34a' }}></i>
            </div>
            <h2 style={{ fontWeight: 900, color: '#16a34a', marginBottom: '8px', fontSize: '1.25rem' }}>Pagamento Confirmado!</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px', fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.5 }}>
                {isGestorMode ? 'Clique abaixo para registrar o pedido.' : 'Clique abaixo para finalizar e receber o resumo do seu pedido no WhatsApp.'}
            </p>
            {isGestorMode ? (
                <button
                    onClick={handleFinalizeOrder}
                    style={{ width: '100%', padding: '16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '16px', fontWeight: 900, fontSize: '1rem', cursor: 'pointer' }}
                >
                    FINALIZAR
                </button>
            ) : (
                <button
                    onClick={handleFinalizeOrder}
                    style={{ width: '100%', padding: '16px', background: '#25d366', color: 'white', border: 'none', borderRadius: '16px', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                >
                    <i className="fab fa-whatsapp" style={{ fontSize: '1.3rem' }}></i>
                    FINALIZAR PEDIDO NO WHATSAPP
                </button>
            )}
        </div>
      )}

      {paymentStep === 'success' && (
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: '16px' }}>✅</div>
            <h2 style={{ fontWeight: 900, color: '#10b981', marginBottom: '8px', fontSize: '1.25rem' }}>Pedido Finalizado!</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px', fontWeight: 600 }}>Seu pedido foi recebido e está sendo preparado.</p>
            <button
                onClick={onClose}
                style={{ width: '100%', padding: '16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '16px', fontWeight: 900, fontSize: '1rem', cursor: 'pointer' }}
            >
                FECHAR
            </button>
        </div>
      )}
    </>
  );

  const modalTitle = paymentStep === 'pix' ? 'Pagamento PIX' : paymentStep === 'classic-pix' ? 'Pagamento PIX' : paymentStep === 'payment' ? 'Pagamento com Cartão' : paymentStep === 'payment_confirmed' ? 'Pagamento Confirmado' : paymentStep === 'success' ? 'Pedido Finalizado' : (items.length > 0 ? `Carrinho (${items.length})` : 'Carrinho');
  return <Modal isOpen={isOpen} onClose={onClose} title={modalTitle}>{modalBody}</Modal>;
};
